// GENERATED by helm-console/scripts/ensure-themes.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/themes.ts (policed by
// helm-bridge/scripts/themes-check.mjs + the Bridge smoke's drift guard).
// Regenerated on every console build; edits here are overwritten.
// Only change from the source: the TerminalColors type import points at
// pi-stack directly instead of helm-bridge's re-exporting types.ts.
// themes.ts — the full-application theme registry. A theme skins the whole
// cockpit (the blueprint tokens in styles.css) AND the pi chat / terminal
// palette from one object; the user picks one theme per app mode (Settings →
// two dropdowns: `prefs.themeDark` / `prefs.themeLight`) and the Dark/Light
// toggle switches which slot renders. Successor to terminal-presets.ts, one
// level up: presets skinned only the chat/terminal; themes skin everything.
//
// HARD CONSTRAINT: this module must stay VALUE-IMPORT-FREE (`import type`
// only). smoke.mjs transpiles it standalone with esbuild and imports the temp
// module directly, so any runtime import would fail to resolve — that smoke
// failure is the guard, keep it. Every color is a precomputed hex literal;
// no runtime color math.
//
// Palette provenance: the borrowed themes carry their upstream family names
// (Catppuccin, Nord, Tokyo Night, Rosé Pine, Gruvbox, Everforest, Kanagawa,
// Dracula, Solarized, Flexoki, GitHub Primer — all MIT) with surfaces
// hand-tuned onto the Bridge's 7-step ground ramp. The two built-ins are the
// blueprint looks and are asserted non-drifting against styles.css by smoke.

import type { TerminalColors } from 'pi-stack/client/term-types.js';

/** The 22 theme-bearing UI tokens — exactly the color tokens that differ
 * between the dark `:root` and light `:root[data-theme="light"]` blocks in
 * styles.css (ground ramp + brand + attention text + the aux palette).
 * Everything else either derives via color-mix (glass), is keyed to the
 * MODE signal (`--glow`, `--hatch-op`, `--star-op`, `--chartline-op`,
 * color-scheme), or is a per-mode semantic set a theme may only re-pin via
 * `overrides`. */
export interface UiTokens {
  bg: string;
  bgDeep: string;
  surface: string;
  surface2: string;
  surface3: string;
  border: string;
  border2: string;
  ink: string;
  ink2: string;
  ink3: string;
  accent: string;
  accent2: string;
  /** accent at ~18% alpha, precomputed 8-digit hex (…2e dark / …24 light). */
  accentDim: string;
  sea: string;
  /** Text on accent-filled controls — contrast-coupled to `accent`. */
  onaccent: string;
  /** Attention TEXT color (`--yellow`): gold on dark grounds, dark amber on
   * light ones. Distinct from the invariant `--yellow-fill` chip fill. */
  yellow: string;
  /** AUXILIARY palette — five general-purpose differentiated colors + a
   * dimmed sixth, per-theme-harmonized (unlike the mode-shared CVD ladder
   * --c1..--c7). NOT syntax-named on purpose: anything needing a small set
   * of distinct hues may ride them. The chat transcript's code rendering
   * maps token roles onto them (keyword→aux1, string→aux2, number/atom→aux3,
   * def/function→aux4, variable/property→aux5, comment→auxDim); legibility
   * floor vs BOTH grounds (ui.bg and chat.background) is gated by
   * scripts/themes-check.mjs: aux1-5 ≥ 3.0, auxDim ≥ 2.5. */
  aux1: string;
  aux2: string;
  aux3: string;
  aux4: string;
  aux5: string;
  auxDim: string;
}

/** ui key → CSS custom property. */
export const UI_TOKEN_VARS: Record<keyof UiTokens, string> = {
  bg: '--bg',
  bgDeep: '--bg-deep',
  surface: '--surface',
  surface2: '--surface-2',
  surface3: '--surface-3',
  border: '--border',
  border2: '--border-2',
  ink: '--ink',
  ink2: '--ink-2',
  ink3: '--ink-3',
  accent: '--accent',
  accent2: '--accent-2',
  accentDim: '--accent-dim',
  sea: '--sea',
  onaccent: '--onaccent',
  yellow: '--yellow',
  aux1: '--aux-1',
  aux2: '--aux-2',
  aux3: '--aux-3',
  aux4: '--aux-4',
  aux5: '--aux-5',
  auxDim: '--aux-dim',
};

/** Tokens a theme may re-pin via `overrides` — the per-mode semantic sets
 * (status ladder, series, domains, planes: CVD-validated per mode, so themes
 * inherit rather than replace them) plus the invariant fills, the CHANGES
 * scope chips, and the legacy terminal-chrome tokens. v1 themes ship none. */
export const OVERRIDABLE_TOKENS = [
  // status chips + work-item ladder rungs
  '--s-decided', '--s-proven', '--s-theory', '--s-open', '--s-risk',
  '--s-built', '--s-stub', '--s-future',
  '--s-scoping', '--s-scoped', '--s-planning', '--s-planned',
  '--s-implementing', '--s-implemented', '--s-re-implementing',
  '--s-testing', '--s-tested', '--s-completed', '--s-aborted',
  // categorical series
  '--c1', '--c2', '--c3', '--c4', '--c5', '--c6', '--c7',
  // metamodel domains
  '--d-schema', '--d-process', '--d-org', '--d-fn', '--d-knowledge',
  '--d-eval', '--d-tools',
  // engineer domains (dev-env W9) — dispatched-pane accents + soft twins
  '--dom-frontend', '--dom-backend', '--dom-database', '--dom-devops',
  '--dom-harness',
  '--dom-frontend-bg', '--dom-backend-bg', '--dom-database-bg',
  '--dom-devops-bg', '--dom-harness-bg',
  // planes
  '--p-control', '--p-exec', '--p-storage',
  // invariant attention fill + CHANGES scope chips
  '--yellow-fill', '--sc-feature', '--sc-epic', '--sc-project',
  // legacy terminal chrome (retires with xterm)
  '--t-green', '--t-green-deep', '--t-yellow', '--t-coral',
  '--t-ink', '--t-muted', '--t-strong',
  '--term-bg', '--term-line', '--term-shadow',
] as const;
export type OverridableToken = (typeof OVERRIDABLE_TOKENS)[number];

/** The identity style's id. Hardcoded rather than imported: this module is
 * VALUE-IMPORT-FREE by contract (see the header), and `import { … } from
 * './styles.js'` is exactly the value import the console copy refuses. The
 * two are pinned to each other by the smoke instead. */
export const IDENTITY_STYLE_ID = 'blueprint';

export interface AppTheme {
  /** kebab-case slug, e.g. 'catppuccin-mocha' — persisted in prefs. */
  id: string;
  /** dropdown label, e.g. 'Catppuccin Mocha'. */
  label: string;
  mode: 'dark' | 'light';
  /** The STYLE this theme is curated FOR (Styles × Themes program: a theme
   * is a palette living inside a style, never a free-floating one). Absent
   * means the identity style — which is what makes adding a style's themes
   * additive: the twenty Blueprint themes say nothing and keep behaving
   * exactly as they did, and a style's own themes are invisible until that
   * style is the one being painted. */
  styleId?: string;
  /** The two styles.css-native looks: themeCssVars() writes NO vars for them
   * (the stylesheet block serves, so they can never drift at runtime). `ui`
   * stays authoritative for swatches, the theme-color meta, and the smoke
   * drift guard that pins it to styles.css. */
  builtin?: boolean;
  ui: UiTokens;
  /** The chat/terminal palette — consumed unchanged by piCssVars (pi views)
   * and buildXtermTheme (remaining xterm surfaces). `background` matches
   * `ui.bg` (or a deliberate near-neighbor) so chat and chrome agree. */
  chat: TerminalColors;
  /** Optional send/working accent (`--send`); unset ⇒ the terracotta default
   * declared in styles.css. */
  send?: string;
  overrides?: Partial<Record<OverridableToken, string>>;
}

// ---------------------------------------------------------------------
// Built-ins — the blueprint looks, extracted verbatim from styles.css.
// Their chat palettes are the Solarized pair (the historical chat defaults,
// carried over from terminal-presets.ts so nobody's chat changes color).
// ---------------------------------------------------------------------

const SOLARIZED_DARK_CHAT: TerminalColors = {
  background: '#002b36',
  foreground: '#839496',
  cursor: '#839496',
  cursorAccent: '#002b36',
  selectionBackground: '#073642',
  black: '#073642',
  red: '#dc322f',
  green: '#859900',
  yellow: '#b58900',
  blue: '#268bd2',
  magenta: '#d33682',
  cyan: '#2aa198',
  white: '#eee8d5',
  brightBlack: '#002b36',
  brightRed: '#cb4b16',
  brightGreen: '#586e75',
  brightYellow: '#657b83',
  brightBlue: '#839496',
  brightMagenta: '#6c71c4',
  brightCyan: '#93a1a1',
  brightWhite: '#fdf6e3',
};

const SOLARIZED_LIGHT_CHAT: TerminalColors = {
  background: '#fdf6e3',
  foreground: '#657b83',
  cursor: '#657b83',
  cursorAccent: '#fdf6e3',
  selectionBackground: '#eee8d5',
  black: '#073642',
  red: '#dc322f',
  green: '#859900',
  yellow: '#b58900',
  blue: '#268bd2',
  magenta: '#d33682',
  cyan: '#2aa198',
  white: '#eee8d5',
  brightBlack: '#002b36',
  brightRed: '#cb4b16',
  brightGreen: '#586e75',
  brightYellow: '#657b83',
  brightBlue: '#839496',
  brightMagenta: '#6c71c4',
  brightCyan: '#93a1a1',
  brightWhite: '#fdf6e3',
};

const MISSION_CONTROL: AppTheme = {
  id: 'mission-control',
  label: 'Mission Control',
  mode: 'dark',
  builtin: true,
  ui: {
    bg: '#0a0e16',
    bgDeep: '#060910',
    surface: '#101624',
    surface2: '#16202f',
    surface3: '#1b2637',
    border: '#1e2a3f',
    border2: '#2a3a55',
    ink: '#dfe7f2',
    ink2: '#93a3ba',
    ink3: '#5c6c84',
    accent: '#83BD4F',
    accent2: '#a3d478',
    accentDim: '#83bd4f2e',
    sea: '#4C8FE8',
    onaccent: '#0c1207',
    yellow: '#FFD66B',
    aux1: '#a48af0', aux2: '#83bd4f', aux3: '#e08a45', aux4: '#4c8fe8', aux5: '#2fa3c2', auxDim: '#5c6c84',
  },
  chat: SOLARIZED_DARK_CHAT,
};

const CHART_TABLE: AppTheme = {
  id: 'chart-table',
  label: 'Chart Table',
  mode: 'light',
  builtin: true,
  ui: {
    bg: '#f4f1ea',
    bgDeep: '#ece8de',
    surface: '#fdfcf8',
    surface2: '#f7f4ec',
    surface3: '#efeadf',
    border: '#d9d2c2',
    border2: '#c4bba6',
    ink: '#1c2430',
    ink2: '#4e5a6c',
    ink3: '#8a94a4',
    accent: '#497F1D',
    accent2: '#3a660f',
    accentDim: '#497f1d24',
    sea: '#1F63C8',
    onaccent: '#fdfcf8',
    yellow: '#B45309',
    aux1: '#7a50c7', aux2: '#497f1d', aux3: '#c05621', aux4: '#1f63c8', aux5: '#0e7490', auxDim: '#8a94a4',
  },
  chat: SOLARIZED_LIGHT_CHAT,
};

// ---------------------------------------------------------------------
// The borrowed roster — 9 dark + 9 light, palettes verified against each
// family's canonical upstream (2026-08-26): catppuccin/palette palette.json,
// nordtheme/nord, folke/tokyonight.nvim extras (windows_terminal night/day),
// rose-pine/palette + rose-pine/alacritty dist, morhetz/gruvbox README,
// sainnhe/everforest autoload palette, rebelot/kanagawa.nvim colors.lua,
// draculatheme.com/spec, altercation/solarized, kepano/flexoki,
// primer/primitives. All MIT. Values marked "tuned" are Bridge-side ramp
// steps or contrast-gated accents (scripts/themes-check.mjs), not upstream
// facts. bright* ANSI slots only feed the deprecated xterm surface.
// ---------------------------------------------------------------------

const CATPPUCCIN_MOCHA: AppTheme = {
  id: 'catppuccin-mocha',
  label: 'Catppuccin Mocha',
  mode: 'dark',
  ui: {
    bg: '#1e1e2e', bgDeep: '#11111b', surface: '#282839', surface2: '#313244',
    surface3: '#45475a', border: '#45475a', border2: '#585b70',
    ink: '#cdd6f4', ink2: '#a6adc8', ink3: '#7f849c',
    accent: '#cba6f7', accent2: '#b4befe', accentDim: '#cba6f72e',
    sea: '#89b4fa', onaccent: '#11111b', yellow: '#f9e2af',
    aux1: '#cba6f7', aux2: '#a6e3a1', aux3: '#fab387', aux4: '#89b4fa', aux5: '#b4befe', auxDim: '#7f849c',
  },
  chat: {
    background: '#1e1e2e', foreground: '#cdd6f4', cursor: '#f5e0dc', cursorAccent: '#1e1e2e',
    selectionBackground: '#45475a',
    black: '#45475a', red: '#f38ba8', green: '#a6e3a1', yellow: '#f9e2af',
    blue: '#89b4fa', magenta: '#f5c2e7', cyan: '#94e2d5', white: '#bac2de',
    brightBlack: '#585b70', brightRed: '#f38ba8', brightGreen: '#a6e3a1', brightYellow: '#f9e2af',
    brightBlue: '#89b4fa', brightMagenta: '#f5c2e7', brightCyan: '#94e2d5', brightWhite: '#a6adc8',
  },
};

const DRACULA: AppTheme = {
  id: 'dracula',
  label: 'Dracula',
  mode: 'dark',
  ui: {
    bg: '#282a36', bgDeep: '#21222c', surface: '#323442', surface2: '#3c3f51',
    surface3: '#44475a', border: '#44475a', border2: '#505878',
    ink: '#f8f8f2', ink2: '#bcc2d3', ink3: '#6272a4',
    accent: '#bd93f9', accent2: '#ff79c6', accentDim: '#bd93f92e',
    sea: '#8be9fd', onaccent: '#21222c', yellow: '#f1fa8c',
    aux1: '#ff79c6', aux2: '#f1fa8c', aux3: '#bd93f9', aux4: '#50fa7b', aux5: '#8be9fd', auxDim: '#6272a4',
  },
  // The chat palette is the retired Dracula terminal preset, verbatim.
  chat: {
    background: '#282a36', foreground: '#f8f8f2', cursor: '#f8f8f2', cursorAccent: '#282a36',
    selectionBackground: '#44475a',
    black: '#21222c', red: '#ff5555', green: '#50fa7b', yellow: '#f1fa8c',
    blue: '#bd93f9', magenta: '#ff79c6', cyan: '#8be9fd', white: '#f8f8f2',
    brightBlack: '#6272a4', brightRed: '#ff6e6e', brightGreen: '#69ff94', brightYellow: '#ffffa5',
    brightBlue: '#d6acff', brightMagenta: '#ff92df', brightCyan: '#a4ffff', brightWhite: '#ffffff',
  },
};

const EVERFOREST_DARK: AppTheme = {
  id: 'everforest-dark',
  label: 'Everforest Dark',
  mode: 'dark',
  ui: {
    bg: '#2d353b', bgDeep: '#232a2e', surface: '#343f44', surface2: '#3d484d',
    surface3: '#475258', border: '#475258', border2: '#4f585e',
    ink: '#d3c6aa', ink2: '#9da9a0', ink3: '#859289',
    accent: '#a7c080', accent2: '#b4c28d', accentDim: '#a7c0802e',
    sea: '#7fbbb3', onaccent: '#232a2e', yellow: '#dbbc7f',
    aux1: '#e67e80', aux2: '#a7c080', aux3: '#d699b6', aux4: '#7fbbb3', aux5: '#dbbc7f', auxDim: '#859289',
  },
  chat: {
    background: '#2d353b', foreground: '#d3c6aa', cursor: '#d3c6aa', cursorAccent: '#2d353b',
    selectionBackground: '#475258',
    black: '#475258', red: '#e67e80', green: '#a7c080', yellow: '#dbbc7f',
    blue: '#7fbbb3', magenta: '#d699b6', cyan: '#83c092', white: '#d3c6aa',
    brightBlack: '#859289', brightRed: '#e67e80', brightGreen: '#a7c080', brightYellow: '#dbbc7f',
    brightBlue: '#7fbbb3', brightMagenta: '#d699b6', brightCyan: '#83c092', brightWhite: '#9da9a0',
  },
};

const GRUVBOX_DARK: AppTheme = {
  id: 'gruvbox-dark',
  label: 'Gruvbox Dark',
  mode: 'dark',
  ui: {
    bg: '#282828', bgDeep: '#1d2021', surface: '#32302f', surface2: '#3c3836',
    surface3: '#504945', border: '#504945', border2: '#665c54',
    ink: '#ebdbb2', ink2: '#bdae93', ink3: '#928374',
    accent: '#b8bb26', accent2: '#c7c550', accentDim: '#b8bb262e',
    sea: '#83a598', onaccent: '#1d2021', yellow: '#fabd2f',
    aux1: '#fb4934', aux2: '#b8bb26', aux3: '#d3869b', aux4: '#fabd2f', aux5: '#83a598', auxDim: '#928374',
  },
  chat: {
    background: '#282828', foreground: '#ebdbb2', cursor: '#ebdbb2', cursorAccent: '#282828',
    selectionBackground: '#504945',
    black: '#282828', red: '#cc241d', green: '#98971a', yellow: '#d79921',
    blue: '#458588', magenta: '#b16286', cyan: '#689d6a', white: '#a89984',
    brightBlack: '#928374', brightRed: '#fb4934', brightGreen: '#b8bb26', brightYellow: '#fabd2f',
    brightBlue: '#83a598', brightMagenta: '#d3869b', brightCyan: '#8ec07c', brightWhite: '#ebdbb2',
  },
};

const KANAGAWA_WAVE: AppTheme = {
  id: 'kanagawa-wave',
  label: 'Kanagawa Wave',
  mode: 'dark',
  ui: {
    bg: '#1f1f28', bgDeep: '#16161d', surface: '#2a2a37', surface2: '#363646',
    surface3: '#424256', border: '#424256', border2: '#54546d',
    ink: '#dcd7ba', ink2: '#c8c093', ink3: '#727169',
    accent: '#7e9cd8', accent2: '#a3d4d5', accentDim: '#7e9cd82e',
    sea: '#7fb4ca', onaccent: '#16161d', yellow: '#e6c384',
    aux1: '#957fb8', aux2: '#98bb6c', aux3: '#d27e99', aux4: '#7e9cd8', aux5: '#e6c384', auxDim: '#727169',
  },
  chat: {
    background: '#1f1f28', foreground: '#dcd7ba', cursor: '#c8c093', cursorAccent: '#1f1f28',
    selectionBackground: '#2d4f67',
    black: '#16161d', red: '#c34043', green: '#76946a', yellow: '#c0a36e',
    blue: '#7e9cd8', magenta: '#957fb8', cyan: '#6a9589', white: '#c8c093',
    brightBlack: '#727169', brightRed: '#e82424', brightGreen: '#98bb6c', brightYellow: '#e6c384',
    brightBlue: '#7fb4ca', brightMagenta: '#938aa9', brightCyan: '#7aa89f', brightWhite: '#dcd7ba',
  },
};

const NORD: AppTheme = {
  id: 'nord',
  label: 'Nord',
  mode: 'dark',
  ui: {
    bg: '#2e3440', bgDeep: '#272c36', surface: '#3b4252', surface2: '#434c5e',
    surface3: '#4c566a', border: '#4c566a', border2: '#616a7d',
    ink: '#eceff4', ink2: '#d8dee9', ink3: '#7b88a1',
    accent: '#88c0d0', accent2: '#8fbcbb', accentDim: '#88c0d02e',
    sea: '#81a1c1', onaccent: '#2e3440', yellow: '#ebcb8b',
    aux1: '#81a1c1', aux2: '#a3be8c', aux3: '#b48ead', aux4: '#88c0d0', aux5: '#d08770', auxDim: '#7b88a1',
  },
  chat: {
    background: '#2e3440', foreground: '#d8dee9', cursor: '#d8dee9', cursorAccent: '#2e3440',
    selectionBackground: '#434c5e',
    black: '#3b4252', red: '#bf616a', green: '#a3be8c', yellow: '#ebcb8b',
    blue: '#81a1c1', magenta: '#b48ead', cyan: '#88c0d0', white: '#e5e9f0',
    brightBlack: '#4c566a', brightRed: '#bf616a', brightGreen: '#a3be8c', brightYellow: '#ebcb8b',
    brightBlue: '#81a1c1', brightMagenta: '#b48ead', brightCyan: '#8fbcbb', brightWhite: '#eceff4',
  },
};

const ROSE_PINE: AppTheme = {
  id: 'rose-pine',
  label: 'Rosé Pine',
  mode: 'dark',
  ui: {
    bg: '#191724', bgDeep: '#14121d', surface: '#1f1d2e', surface2: '#26233a',
    surface3: '#403d52', border: '#403d52', border2: '#524f67',
    ink: '#e0def4', ink2: '#908caa', ink3: '#6e6a86',
    accent: '#c4a7e7', accent2: '#d4c1ef', accentDim: '#c4a7e72e',
    sea: '#9ccfd8', onaccent: '#191724', yellow: '#f6c177',
    aux1: '#c4a7e7', aux2: '#f6c177', aux3: '#ebbcba', aux4: '#9ccfd8', aux5: '#3e8fb0', auxDim: '#6e6a86',
  },
  chat: {
    background: '#191724', foreground: '#e0def4', cursor: '#e0def4', cursorAccent: '#191724',
    selectionBackground: '#403d52',
    black: '#26233a', red: '#eb6f92', green: '#31748f', yellow: '#f6c177',
    blue: '#9ccfd8', magenta: '#c4a7e7', cyan: '#ebbcba', white: '#e0def4',
    brightBlack: '#6e6a86', brightRed: '#eb6f92', brightGreen: '#31748f', brightYellow: '#f6c177',
    brightBlue: '#9ccfd8', brightMagenta: '#c4a7e7', brightCyan: '#ebbcba', brightWhite: '#e0def4',
  },
};

const SOLARIZED_DARK: AppTheme = {
  id: 'solarized-dark',
  label: 'Solarized Dark',
  mode: 'dark',
  ui: {
    bg: '#002b36', bgDeep: '#00222b', surface: '#073642', surface2: '#0d3d49',
    surface3: '#144450', border: '#144450', border2: '#29525d',
    ink: '#eee8d5', ink2: '#93a1a1', ink3: '#657b83',
    accent: '#268bd2', accent2: '#4ca6e8', accentDim: '#268bd22e',
    sea: '#2aa198', onaccent: '#00222b', yellow: '#b58900',
    aux1: '#859900', aux2: '#2aa198', aux3: '#d33682', aux4: '#268bd2', aux5: '#cb4b16', auxDim: '#657b83',
  },
  chat: SOLARIZED_DARK_CHAT,
};

const TOKYO_NIGHT: AppTheme = {
  id: 'tokyo-night',
  label: 'Tokyo Night',
  mode: 'dark',
  ui: {
    bg: '#1a1b26', bgDeep: '#16161e', surface: '#212434', surface2: '#292e42',
    surface3: '#323852', border: '#3b4261', border2: '#414868',
    ink: '#c0caf5', ink2: '#a9b1d6', ink3: '#565f89',
    accent: '#7aa2f7', accent2: '#7dcfff', accentDim: '#7aa2f72e',
    sea: '#2ac3de', onaccent: '#16161e', yellow: '#e0af68',
    aux1: '#bb9af7', aux2: '#9ece6a', aux3: '#ff9e64', aux4: '#7aa2f7', aux5: '#73daca', auxDim: '#565f89',
  },
  chat: {
    background: '#1a1b26', foreground: '#c0caf5', cursor: '#c0caf5', cursorAccent: '#1a1b26',
    selectionBackground: '#283457',
    black: '#15161e', red: '#f7768e', green: '#9ece6a', yellow: '#e0af68',
    blue: '#7aa2f7', magenta: '#bb9af7', cyan: '#7dcfff', white: '#a9b1d6',
    brightBlack: '#414868', brightRed: '#f7768e', brightGreen: '#9ece6a', brightYellow: '#e0af68',
    brightBlue: '#7aa2f7', brightMagenta: '#9d7cd8', brightCyan: '#7dcfff', brightWhite: '#c0caf5',
  },
};

const CATPPUCCIN_LATTE: AppTheme = {
  id: 'catppuccin-latte',
  label: 'Catppuccin Latte',
  mode: 'light',
  ui: {
    bg: '#e6e9ef', bgDeep: '#dce0e8', surface: '#eff1f5', surface2: '#ebedf2',
    surface3: '#dce0e8', border: '#ccd0da', border2: '#bcc0cc',
    ink: '#4c4f69', ink2: '#6c6f85', ink3: '#8c8fa1',
    accent: '#8839ef', accent2: '#6f22cd', accentDim: '#8839ef24',
    sea: '#1e66f5', onaccent: '#eff1f5', yellow: '#8f5c00',
    aux1: '#8839ef', aux2: '#357f24', aux3: '#d14e00', aux4: '#1e66f5', aux5: '#179299', auxDim: '#8c8fa1',
  },
  chat: {
    background: '#eff1f5', foreground: '#4c4f69', cursor: '#dc8a78', cursorAccent: '#eff1f5',
    selectionBackground: '#bcc0cc',
    black: '#bcc0cc', red: '#d20f39', green: '#40a02b', yellow: '#df8e1d',
    blue: '#1e66f5', magenta: '#ea76cb', cyan: '#179299', white: '#5c5f77',
    brightBlack: '#acb0be', brightRed: '#d20f39', brightGreen: '#40a02b', brightYellow: '#df8e1d',
    brightBlue: '#1e66f5', brightMagenta: '#ea76cb', brightCyan: '#179299', brightWhite: '#6c6f85',
  },
};

const EVERFOREST_LIGHT: AppTheme = {
  id: 'everforest-light',
  label: 'Everforest Light',
  mode: 'light',
  ui: {
    bg: '#fdf6e3', bgDeep: '#efebd4', surface: '#fffbef', surface2: '#f4f0d9',
    surface3: '#efebd4', border: '#e6e2cc', border2: '#bdc3af',
    ink: '#5c6a72', ink2: '#829181', ink3: '#939f91',
    // accent tuned darker than canonical green #8da101 (2.7:1 on the cream
    // ground) — the canonical hue survives in chat/status; hue kept.
    accent: '#5f7509', accent2: '#4d6007', accentDim: '#5f750924',
    sea: '#3a94c5', onaccent: '#fffbef', yellow: '#946c00',
    aux1: '#e13b3f', aux2: '#708603', aux3: '#c2479c', aux4: '#2f7fae', aux5: '#2b8a66', auxDim: '#939f91',
  },
  chat: {
    background: '#fdf6e3', foreground: '#5c6a72', cursor: '#5c6a72', cursorAccent: '#fdf6e3',
    selectionBackground: '#e6e2cc',
    black: '#efebd4', red: '#f85552', green: '#8da101', yellow: '#dfa000',
    blue: '#3a94c5', magenta: '#df69ba', cyan: '#35a77c', white: '#5c6a72',
    brightBlack: '#939f91', brightRed: '#f85552', brightGreen: '#8da101', brightYellow: '#dfa000',
    brightBlue: '#3a94c5', brightMagenta: '#df69ba', brightCyan: '#35a77c', brightWhite: '#5c6a72',
  },
};

const FLEXOKI_LIGHT: AppTheme = {
  id: 'flexoki-light',
  label: 'Flexoki Light',
  mode: 'light',
  ui: {
    bg: '#f2f0e5', bgDeep: '#e6e4d9', surface: '#fffcf0', surface2: '#f8f6eb',
    surface3: '#e6e4d9', border: '#dad8ce', border2: '#b7b5ac',
    ink: '#100f0f', ink2: '#6f6e69', ink3: '#878580',
    // accent + yellow tuned darker than green-600/yellow-600 for text
    // contrast on paper; canonical tiers survive in the chat palette.
    accent: '#5f7708', accent2: '#4d6106', accentDim: '#5f770824',
    sea: '#205ea6', onaccent: '#fffcf0', yellow: '#8a6702',
    aux1: '#5e409d', aux2: '#66800b', aux3: '#8a6702', aux4: '#205ea6', aux5: '#24837b', auxDim: '#878580',
  },
  chat: {
    background: '#fffcf0', foreground: '#100f0f', cursor: '#100f0f', cursorAccent: '#fffcf0',
    selectionBackground: '#dad8ce',
    black: '#100f0f', red: '#af3029', green: '#66800b', yellow: '#ad8301',
    blue: '#205ea6', magenta: '#a02f6f', cyan: '#24837b', white: '#6f6e69',
    brightBlack: '#878580', brightRed: '#d14d41', brightGreen: '#879a39', brightYellow: '#d0a215',
    brightBlue: '#4385be', brightMagenta: '#ce5d97', brightCyan: '#3aa99f', brightWhite: '#cecdc3',
  },
};

const GITHUB_LIGHT: AppTheme = {
  id: 'github-light',
  label: 'GitHub Light',
  mode: 'light',
  ui: {
    bg: '#f6f8fa', bgDeep: '#eaeef2', surface: '#ffffff', surface2: '#fafbfc',
    surface3: '#eaeef2', border: '#d0d7de', border2: '#afb8c1',
    ink: '#1f2328', ink2: '#57606a', ink3: '#6e7781',
    accent: '#1a7f37', accent2: '#116329', accentDim: '#1a7f3724',
    sea: '#0969da', onaccent: '#ffffff', yellow: '#9a6700',
    aux1: '#cf222e', aux2: '#0a3069', aux3: '#0550ae', aux4: '#8250df', aux5: '#953800', auxDim: '#6e7781',
  },
  // The chat palette is the retired GitHub terminal preset, verbatim.
  chat: {
    background: '#ffffff', foreground: '#24292e', cursor: '#24292e', cursorAccent: '#ffffff',
    selectionBackground: '#c8e1ff',
    black: '#24292e', red: '#d73a49', green: '#22863a', yellow: '#b08800',
    blue: '#0366d6', magenta: '#6f42c1', cyan: '#1b7c83', white: '#6a737d',
    brightBlack: '#959da5', brightRed: '#cb2431', brightGreen: '#28a745', brightYellow: '#dbab09',
    brightBlue: '#2188ff', brightMagenta: '#8a63d2', brightCyan: '#3192aa', brightWhite: '#d1d5da',
  },
};

const GRUVBOX_LIGHT: AppTheme = {
  id: 'gruvbox-light',
  label: 'Gruvbox Light',
  mode: 'light',
  ui: {
    bg: '#fbf1c7', bgDeep: '#f2e5bc', surface: '#f9f5d7', surface2: '#f5edc8',
    surface3: '#f2e5bc', border: '#ebdbb2', border2: '#d5c4a1',
    ink: '#3c3836', ink2: '#665c54', ink3: '#928374',
    accent: '#79740e', accent2: '#625e0b', accentDim: '#79740e24',
    // white button ink + a darker amber than faded-yellow #b57614 (3.3:1).
    sea: '#076678', onaccent: '#ffffff', yellow: '#94600c',
    aux1: '#9d0006', aux2: '#79740e', aux3: '#8f3f71', aux4: '#b57614', aux5: '#076678', auxDim: '#928374',
  },
  chat: {
    background: '#fbf1c7', foreground: '#3c3836', cursor: '#3c3836', cursorAccent: '#fbf1c7',
    selectionBackground: '#d5c4a1',
    black: '#fbf1c7', red: '#cc241d', green: '#98971a', yellow: '#d79921',
    blue: '#458588', magenta: '#b16286', cyan: '#689d6a', white: '#7c6f64',
    brightBlack: '#928374', brightRed: '#9d0006', brightGreen: '#79740e', brightYellow: '#b57614',
    brightBlue: '#076678', brightMagenta: '#8f3f71', brightCyan: '#427b58', brightWhite: '#3c3836',
  },
};

const KANAGAWA_LOTUS: AppTheme = {
  id: 'kanagawa-lotus',
  label: 'Kanagawa Lotus',
  mode: 'light',
  ui: {
    bg: '#f2ecbc', bgDeep: '#e5ddb0', surface: '#f7f2cd', surface2: '#ece5b0',
    surface3: '#e7dba0', border: '#d5cea3', border2: '#c4bd92',
    ink: '#545464', ink2: '#716e61', ink3: '#8a8980',
    accent: '#4d699b', accent2: '#3e5680', accentDim: '#4d699b24',
    sea: '#4e8ca2', onaccent: '#f7f2cd', yellow: '#9c5300',
    aux1: '#624c83', aux2: '#6f894e', aux3: '#b35b79', aux4: '#4d699b', aux5: '#77713f', auxDim: '#8a8980',
  },
  chat: {
    background: '#f2ecbc', foreground: '#545464', cursor: '#545464', cursorAccent: '#f2ecbc',
    selectionBackground: '#c9cbd1',
    black: '#d5cea3', red: '#c84053', green: '#6f894e', yellow: '#77713f',
    blue: '#4d699b', magenta: '#b35b79', cyan: '#597b75', white: '#545464',
    brightBlack: '#8a8980', brightRed: '#d7474b', brightGreen: '#6e915f', brightYellow: '#836f4a',
    brightBlue: '#6693bf', brightMagenta: '#624c83', brightCyan: '#5e857a', brightWhite: '#43436c',
  },
};

const ROSE_PINE_DAWN: AppTheme = {
  id: 'rose-pine-dawn',
  label: 'Rosé Pine Dawn',
  mode: 'light',
  ui: {
    bg: '#faf4ed', bgDeep: '#f2e9e1', surface: '#fffaf3', surface2: '#f4ede8',
    surface3: '#eae4e1', border: '#dfdad9', border2: '#cecacd',
    ink: '#575279', ink2: '#797593', ink3: '#8e899c',
    // iris darkened one step (canonical #907aa9 traps button ink at 3.6:1).
    accent: '#7a6392', accent2: '#665081', accentDim: '#7a639224',
    sea: '#286983', onaccent: '#fffaf3', yellow: '#8f5b00',
    aux1: '#907aa9', aux2: '#a3721c', aux3: '#b4637a', aux4: '#286983', aux5: '#56949f', auxDim: '#8e899c',
  },
  chat: {
    background: '#faf4ed', foreground: '#575279', cursor: '#575279', cursorAccent: '#faf4ed',
    selectionBackground: '#dfdad9',
    black: '#f2e9e1', red: '#b4637a', green: '#286983', yellow: '#ea9d34',
    blue: '#56949f', magenta: '#907aa9', cyan: '#d7827e', white: '#575279',
    brightBlack: '#9893a5', brightRed: '#b4637a', brightGreen: '#286983', brightYellow: '#ea9d34',
    brightBlue: '#56949f', brightMagenta: '#907aa9', brightCyan: '#d7827e', brightWhite: '#575279',
  },
};

const SOLARIZED_LIGHT: AppTheme = {
  id: 'solarized-light',
  label: 'Solarized Light',
  mode: 'light',
  ui: {
    bg: '#f9f2df', bgDeep: '#eee8d5', surface: '#fdf6e3', surface2: '#fbf4e1',
    surface3: '#eee8d5', border: '#d7d6c8', border2: '#c2c4b4',
    ink: '#073642', ink2: '#586e75', ink3: '#839496',
    // blue darkened one step for light mode (canonical #268bd2 traps button
    // ink at 3.4:1 on paper); the canonical blue keeps the chat accent.
    accent: '#1a6da8', accent2: '#135d92', accentDim: '#1a6da824',
    sea: '#2aa198', onaccent: '#fdf6e3', yellow: '#8f6c00',
    aux1: '#6f8100', aux2: '#22857d', aux3: '#d33682', aux4: '#268bd2', aux5: '#cb4b16', auxDim: '#839496',
  },
  // chat bg = base3 (ui.surface) — the classic Solarized paper, deliberate
  // near-neighbor of the slightly deeper ui ground.
  chat: SOLARIZED_LIGHT_CHAT,
};

const TOKYO_NIGHT_DAY: AppTheme = {
  id: 'tokyo-night-day',
  label: 'Tokyo Night Day',
  mode: 'light',
  ui: {
    bg: '#e1e2e7', bgDeep: '#d4d6e4', surface: '#e9e9ec', surface2: '#dcdee7',
    surface3: '#d2d5e2', border: '#c4c8da', border2: '#a8aecb',
    ink: '#3760bf', ink2: '#6172b0', ink3: '#848cb5',
    // blue darkened one step for button-ink contrast; amber darkened from
    // the canonical #8c6c3e (3.7:1). Canonical hues keep the chat accents.
    accent: '#1a5fc4', accent2: '#164ea3', accentDim: '#1a5fc424',
    sea: '#007197', onaccent: '#e1e2e7', yellow: '#7a5a28',
    aux1: '#7847bd', aux2: '#587539', aux3: '#b15c00', aux4: '#1a5fc4', aux5: '#007197', auxDim: '#848cb5',
  },
  chat: {
    background: '#e1e2e7', foreground: '#3760bf', cursor: '#3760bf', cursorAccent: '#e1e2e7',
    selectionBackground: '#b7c1e3',
    black: '#b4b5b9', red: '#f52a65', green: '#587539', yellow: '#8c6c3e',
    blue: '#2e7de9', magenta: '#9854f1', cyan: '#007197', white: '#6172b0',
    brightBlack: '#a1a6c5', brightRed: '#f52a65', brightGreen: '#587539', brightYellow: '#8c6c3e',
    brightBlue: '#2e7de9', brightMagenta: '#7847bd', brightCyan: '#007197', brightWhite: '#3760bf',
  },
};

// ---------------------------------------------------------------------
// GLASS — the four curated slots of the first non-identity style (PR-5).
//
// Three moods, four slots, and exactly ONE light slot on purpose:
// glow-as-signal needs a dark receiving surface, so Spectral Day is a
// considered frosted-daylight reinterpretation rather than a fourth mood.
// Tide Pool and Aniline register dark only — themesForMode() serves two
// independent per-mode dropdowns, so a dark-only theme simply does not
// appear in the light list. The Dark/Light toggle is NOT greyed out.
//
// Every hex below is transcribed from the approved spec (WF-B
// specs/glass.md §2), whose palettes were floor-verified against
// scripts/themes-check.mjs before approval — 4 slots, 0 failures.
// ---------------------------------------------------------------------

/** Optical violet — the reference GLASS look, rendered as instrumentation. */
const GLASS_SPECTRAL: AppTheme = {
  id: 'glass-spectral',
  label: 'Spectral',
  mode: 'dark',
  styleId: 'glass',
  ui: {
    bg: '#0A0714', bgDeep: '#050310', surface: '#120E20', surface2: '#191430',
    surface3: '#221B40', border: '#2C2350', border2: '#3E3170',
    ink: '#ECE7FB', ink2: '#A79CCB', ink3: '#7A6FA0',
    accent: '#9B5CFF', accent2: '#C3A0FF', accentDim: '#9B5CFF2e',
    sea: '#5C9BFF', onaccent: '#120520', yellow: '#FFDCA0',
    aux1: '#FF5CA8', aux2: '#5CFFE0', aux3: '#FFB55C', aux4: '#7FA8FF', aux5: '#C9A6FF', auxDim: '#7A6FA0',
  },
  chat: {
    background: '#0A0714', foreground: '#ECE7FB', cursor: '#9B5CFF', cursorAccent: '#0A0714',
    selectionBackground: '#221B40',
    black: '#221B40', red: '#FF5C7A', green: '#7BE8A8', yellow: '#FFDCA0',
    blue: '#5C9BFF', magenta: '#FF5CA8', cyan: '#5CFFE0', white: '#A79CCB',
    brightBlack: '#3E3170', brightRed: '#FF89A0', brightGreen: '#A2F2C4', brightYellow: '#FFE9C4',
    brightBlue: '#8FBAFF', brightMagenta: '#FF8FC4', brightCyan: '#93FFEC', brightWhite: '#ECE7FB',
  },
};

/** The only light slot GLASS ships, and deliberately so (spec §2). */
const GLASS_SPECTRAL_DAY: AppTheme = {
  id: 'glass-spectral-day',
  label: 'Spectral Day',
  mode: 'light',
  styleId: 'glass',
  ui: {
    bg: '#F4F1FB', bgDeep: '#E8E3F5', surface: '#FFFFFF', surface2: '#F8F5FD',
    surface3: '#EFEAF9', border: '#DCD3F0', border2: '#C0B2E0',
    ink: '#1E1730', ink2: '#4E4470', ink3: '#7B71A0',
    accent: '#6C2BD9', accent2: '#5620B0', accentDim: '#6C2BD924',
    sea: '#1F5FD6', onaccent: '#FFFFFF', yellow: '#6F5A0E',
    aux1: '#B4176B', aux2: '#0E7A6B', aux3: '#9A5A00', aux4: '#2A5AC8', aux5: '#6B3FBF', auxDim: '#7B71A0',
  },
  chat: {
    background: '#F4F1FB', foreground: '#1E1730', cursor: '#6C2BD9', cursorAccent: '#F4F1FB',
    selectionBackground: '#DCD3F0',
    black: '#1E1730', red: '#B4176B', green: '#0E7A6B', yellow: '#9A5A00',
    blue: '#2A5AC8', magenta: '#6B3FBF', cyan: '#0F6E8C', white: '#4E4470',
    brightBlack: '#7B71A0', brightRed: '#C93B85', brightGreen: '#12907E', brightYellow: '#B06A00',
    brightBlue: '#3A6BD8', brightMagenta: '#7D4FCF', brightCyan: '#1782A2', brightWhite: '#1E1730',
  },
};

/** Bioluminescent green-teal with a magenta counter-glow. Hue ~170°,
 * deliberately not Starship's blue-cyan HUD (~187°, ruling R17), on a
 * green-black rather than a navy ground. */
const GLASS_TIDE_POOL: AppTheme = {
  id: 'glass-tide-pool',
  label: 'Tide Pool',
  mode: 'dark',
  styleId: 'glass',
  ui: {
    bg: '#04110F', bgDeep: '#020A09', surface: '#0A1D1A', surface2: '#0F2724',
    surface3: '#15332F', border: '#1C4139', border2: '#2A5C50',
    ink: '#DCF6EF', ink2: '#8FBFB4', ink3: '#688F86',
    accent: '#2EE6C8', accent2: '#7FF3DF', accentDim: '#2EE6C82e',
    sea: '#4FB8E8', onaccent: '#04211C', yellow: '#F5DEA8',
    aux1: '#FF4FA3', aux2: '#7FE87F', aux3: '#FFA85C', aux4: '#6FC4FF', aux5: '#C9A6FF', auxDim: '#688F86',
  },
  chat: {
    background: '#04110F', foreground: '#DCF6EF', cursor: '#2EE6C8', cursorAccent: '#04110F',
    selectionBackground: '#15332F',
    black: '#15332F', red: '#FF4FA3', green: '#7FE87F', yellow: '#F5DEA8',
    blue: '#4FB8E8', magenta: '#E07FE8', cyan: '#2EE6C8', white: '#8FBFB4',
    brightBlack: '#2A5C50', brightRed: '#FF7FBB', brightGreen: '#A6F0A6', brightYellow: '#FAEBC9',
    brightBlue: '#7FCEF0', brightMagenta: '#EDA6F2', brightCyan: '#7FF3DF', brightWhite: '#DCF6EF',
  },
};

/** Magenta-mauve, and the reason this slot exists: magenta is the one hue no
 * prism produces — it appears only where both ends of a spectrum overlap,
 * which is exactly what a dispersion fringe does. The most on-thesis colour a
 * dispersion style can own, and it collides with nothing else in the
 * programme (cross-style ruling R6). */
const GLASS_ANILINE: AppTheme = {
  id: 'glass-aniline',
  label: 'Aniline',
  mode: 'dark',
  styleId: 'glass',
  ui: {
    bg: '#0F0715', bgDeep: '#080310', surface: '#1A0E22', surface2: '#23142E',
    surface3: '#2D1A3A', border: '#3A2149', border2: '#512F66',
    ink: '#F7E7FA', ink2: '#C4A0CE', ink3: '#96739E',
    accent: '#E85CD6', accent2: '#F79CEA', accentDim: '#E85CD62e',
    sea: '#6FB4FF', onaccent: '#1C0418', yellow: '#FFDCA0',
    aux1: '#6FE8D2', aux2: '#FFA85C', aux3: '#8FA8FF', aux4: '#FF7FA8', aux5: '#C9FF8C', auxDim: '#96739E',
  },
  chat: {
    background: '#0F0715', foreground: '#F7E7FA', cursor: '#E85CD6', cursorAccent: '#0F0715',
    selectionBackground: '#2D1A3A',
    black: '#2D1A3A', red: '#FF7FA8', green: '#C9FF8C', yellow: '#FFA85C',
    blue: '#8FA8FF', magenta: '#E85CD6', cyan: '#6FE8D2', white: '#C4A0CE',
    brightBlack: '#512F66', brightRed: '#FFA6C4', brightGreen: '#DBFFB3', brightYellow: '#FFC48F',
    brightBlue: '#B3C4FF', brightMagenta: '#F79CEA', brightCyan: '#9CF0E1', brightWhite: '#F7E7FA',
  },
};

// ---------------------------------------------------------------------
// SYNTHWAVE '88 — three curated slots, and NO LIGHT SLOT (PR-11).
//
// R19, the standing ruling four attempts across two rounds reached for: no
// style may disable, gray out, or redefine the global Dark/Light toggle.
// SYNTHWAVE has no daylight identity — a lit outline over a dark plate is the
// whole material contract and there is no honest daytime reading of it — so
// it registers THREE DARK SLOTS AND NO LIGHT ONE, and that is the entire
// mechanism: themesForMode() serves two independent per-mode dropdowns, so a
// style with no light theme simply does not appear in the light list. The
// toggle stays fully enabled and keeps meaning exactly what it means
// everywhere else; the Settings light slot carries an INFO line naming the
// fallback rather than a disabled control (app.ts, and the smoke checks the
// line exists from the other side).
//
// R23 — SYNTHWAVE IS THE ONLY CHROMATIC GROUND IN THE PROGRAMME. Measured raw
// chroma of `bg`: SYNTHWAVE 15.7, every other style ≤5.1. That is both the
// identity rule and the guardrail: no other style may ship a ground above 8,
// and these three must stay above 12, so they can never drift toward a
// neutral dark that would collide with Studio dark or Starship's void. The
// smoke measures the chroma of all three rather than trusting the comment.
//
// R17 — THE ACCENT IS NEVER CYAN. All three source attempts put their accent
// within 1° of Starship's HUD cyan (hue 187); cyan is SYNTHWAVE's
// COUNTER-hue and lives in `--sea`, the aux set and the icon alternation.
// These three accents sit at hue 325 / 279 / 225.
//
// Every hex below is transcribed from the approved spec (WF-B
// specs/synthwave.md §2), whose palettes were floor-verified against
// scripts/themes-check.mjs before approval — 3 slots, 0 failures.
// ---------------------------------------------------------------------

/** Night Drive — the owner's `#1a1038` anchor: hot pink on indigo with cyan
 * running against it. The reference SYNTHWAVE look and the style's default. */
const SYNTHWAVE_NIGHT_DRIVE: AppTheme = {
  id: 'synthwave-night-drive',
  label: 'Night Drive',
  mode: 'dark',
  styleId: 'synthwave',
  ui: {
    bg: '#1A1038', bgDeep: '#100A26', surface: '#201548', surface2: '#281B57',
    surface3: '#322166', border: '#3D2C78', border2: '#56409E',
    ink: '#F1ECFF', ink2: '#B6A8E6', ink3: '#8878C0',
    accent: '#FF2FA8', accent2: '#FF85CC', accentDim: '#FF2FA82e',
    sea: '#2DE2FF', onaccent: '#1A0313', yellow: '#FFD98A',
    aux1: '#2DE2FF', aux2: '#45F0D5', aux3: '#FFA24F', aux4: '#9B7BFF', aux5: '#FF6FE8', auxDim: '#8878C0',
  },
  chat: {
    background: '#1A1038', foreground: '#F1ECFF', cursor: '#FF2FA8', cursorAccent: '#1A1038',
    selectionBackground: '#322166',
    black: '#322166', red: '#FF5C7A', green: '#7CF0A8', yellow: '#FFD98A',
    blue: '#2DE2FF', magenta: '#FF6FE8', cyan: '#45F0D5', white: '#B6A8E6',
    brightBlack: '#56409E', brightRed: '#FF8A9E', brightGreen: '#A6F7C6', brightYellow: '#FFE8B8',
    brightBlue: '#8FEFFF', brightMagenta: '#FF9CF0', brightCyan: '#8CF7E6', brightWhite: '#F1ECFF',
  },
};

/** Ultraviolet — a deeper violet ground, a purple accent, magenta demoted to
 * the counter-hue. Its accent sits 16° from Glass Spectral's violet: the same
 * margin judge 1 accepted between Glass Tide Pool (170) and Starship Void
 * Watch (187), and the two are additionally separated by MATERIAL — an opaque
 * neon outline versus translucent refracting glass. */
const SYNTHWAVE_ULTRAVIOLET: AppTheme = {
  id: 'synthwave-ultraviolet',
  label: 'Ultraviolet',
  mode: 'dark',
  styleId: 'synthwave',
  ui: {
    bg: '#1E1240', bgDeep: '#130A2C', surface: '#26174E', surface2: '#2E1C5C',
    surface3: '#38236C', border: '#43307F', border2: '#5C44A8',
    ink: '#F3EBFF', ink2: '#BCA8E8', ink3: '#8C78C4',
    accent: '#C24DFF', accent2: '#DB9BFF', accentDim: '#C24DFF2e',
    sea: '#2DE2FF', onaccent: '#160030', yellow: '#FFD98A',
    aux1: '#2DE2FF', aux2: '#45F0D5', aux3: '#FFA24F', aux4: '#FF57D2', aux5: '#7BA0FF', auxDim: '#8C78C4',
  },
  chat: {
    background: '#1E1240', foreground: '#F3EBFF', cursor: '#C24DFF', cursorAccent: '#1E1240',
    selectionBackground: '#38236C',
    black: '#38236C', red: '#FF5C7A', green: '#7CF0A8', yellow: '#FFD98A',
    blue: '#7BA0FF', magenta: '#FF57D2', cyan: '#2DE2FF', white: '#BCA8E8',
    brightBlack: '#5C44A8', brightRed: '#FF8A9E', brightGreen: '#A6F7C6', brightYellow: '#FFE8B8',
    brightBlue: '#A8C0FF', brightMagenta: '#FF8FE0', brightCyan: '#8CF7E6', brightWhite: '#F3EBFF',
  },
};

/** Vector — the coldest ground and an electric-blue accent: the
 * wireframe/Battlezone reading of the same formula. Lowest glow temperature
 * of the three, and the long-shift theme. */
const SYNTHWAVE_VECTOR: AppTheme = {
  id: 'synthwave-vector',
  label: 'Vector',
  mode: 'dark',
  styleId: 'synthwave',
  ui: {
    bg: '#120E32', bgDeep: '#0A0722', surface: '#1A1444', surface2: '#211A54',
    surface3: '#292064', border: '#33286F', border2: '#4A3A96',
    ink: '#E8ECFF', ink2: '#A8AEE0', ink3: '#7A80B8',
    accent: '#5C86FF', accent2: '#9DB6FF', accentDim: '#5C86FF2e',
    sea: '#2DE2FF', onaccent: '#050A1E', yellow: '#FFD98A',
    aux1: '#2DE2FF', aux2: '#45F0D5', aux3: '#FFA24F', aux4: '#C08CFF', aux5: '#FF6FC0', auxDim: '#7A80B8',
  },
  chat: {
    background: '#120E32', foreground: '#E8ECFF', cursor: '#5C86FF', cursorAccent: '#120E32',
    selectionBackground: '#292064',
    black: '#292064', red: '#FF5C7A', green: '#7CF0A8', yellow: '#FFD98A',
    blue: '#5C86FF', magenta: '#FF6FC0', cyan: '#2DE2FF', white: '#A8AEE0',
    brightBlack: '#4A3A96', brightRed: '#FF8A9E', brightGreen: '#A6F7C6', brightYellow: '#FFE8B8',
    brightBlue: '#9DB6FF', brightMagenta: '#FF9CD8', brightCyan: '#8CF7E6', brightWhite: '#E8ECFF',
  },
};
// STUDIO — six slots, ONE ground, and the composition is the contract.
//
// The brief's hard rule for the quiet style is that every STUDIO theme shares
// one ground family and themes vary ONLY the primary. Six hand-copied
// palettes would satisfy that on the day they were written and stop
// satisfying it the first time somebody nudged one hex; so the six slots are
// COMPOSED from one ramp object per mode rather than transcribed six times.
// Byte-identity across the three themes in a mode is then a property of the
// code, not a promise in a comment — and the smoke measures it anyway
// (the ground-stability row: 6 slots scanned, 1 distinct ground ramp per
// mode, with a tinted-slot positive control that must refuse).
//
// THE RAMP IS ZERO-CHROMA TO THE BYTE — every one of the ten tokens has
// R = G = B, in both modes. That is not decoration: it is the honest reading
// of "hueless", it is what keeps STUDIO's neutral intent from colliding with
// its own indigo primary (a 240°-tinted grey measured a 1° gap in judging),
// and it is the first of the three greppable separations from Blueprint
// (R18) — Blueprint's grounds measure chroma 4.7 dark / 3.9 light.
//
// Every hex is transcribed from the approved spec (WF-B specs/studio.md §2),
// floor-verified against scripts/themes-check.mjs before approval: 6 slots,
// 0 failures.
// ---------------------------------------------------------------------

/** The shared neutral ramp — ten tokens, R = G = B to the byte, per mode.
 * A theme that touches one of these is a review failure, and the composition
 * below is what makes touching one impossible without editing this object. */
const STUDIO_RAMP: Record<'dark' | 'light', Pick<UiTokens,
  'bg' | 'bgDeep' | 'surface' | 'surface2' | 'surface3' | 'border' | 'border2' | 'ink' | 'ink2' | 'ink3'>> = {
  light: {
    bg: '#FAFAFA', bgDeep: '#F1F1F1', surface: '#FFFFFF', surface2: '#F6F6F6', surface3: '#EEEEEE',
    border: '#E4E4E4', border2: '#D4D4D4',
    ink: '#191919', ink2: '#525252', ink3: '#8C8C8C',
  },
  dark: {
    bg: '#0B0B0B', bgDeep: '#000000', surface: '#151515', surface2: '#1C1C1C', surface3: '#232323',
    border: '#2A2A2A', border2: '#3A3A3A',
    ink: '#F4F4F4', ink2: '#A1A1A1', ink3: '#717171',
  },
};

/** The shared NON-PRIMARY colour set — also identical across all three
 * themes in a mode. `sea` is a deliberately desaturated slate so that even in
 * Cyanotype, where the primary is also blue, the two are told apart by
 * saturation and by MATERIAL: a link is underlined `--sea` text, a primary is
 * always a solid `--accent` fill. Even the link colour is fixed across the
 * three themes. */
const STUDIO_REST: Record<'dark' | 'light', Pick<UiTokens,
  'sea' | 'yellow' | 'aux1' | 'aux2' | 'aux3' | 'aux4' | 'aux5' | 'auxDim'>> = {
  light: {
    sea: '#456277', yellow: '#6E5A0E',
    aux1: '#1F5FA8', aux2: '#14795F', aux3: '#A85A12', aux4: '#6D4AA8', aux5: '#A62B5C', auxDim: '#8C8C8C',
  },
  dark: {
    sea: '#8FADC2', yellow: '#E8D6A8',
    aux1: '#6FA8E8', aux2: '#4FC0A0', aux3: '#E0A05C', aux4: '#A98CE8', aux5: '#E884A8', auxDim: '#717171',
  },
};

/** The only thing a STUDIO theme varies. */
type StudioPrimary = Pick<UiTokens, 'accent' | 'accent2' | 'accentDim' | 'onaccent'>;

/** ramp + shared + the primary. THE composition — three sources, one slot. */
function studioUi(mode: 'dark' | 'light', primary: StudioPrimary): UiTokens {
  return { ...STUDIO_RAMP[mode], ...STUDIO_REST[mode], ...primary };
}

/** The chat/terminal half, composed the same way: one base per mode, and the
 * cursor is the only thing the primary moves — a terminal whose whole palette
 * changed per theme would break the shared-ground contract in the one surface
 * where it is most visible. */
const STUDIO_CHAT_BASE: Record<'dark' | 'light', Omit<TerminalColors, 'cursor'>> = {
  light: {
    background: '#FAFAFA', foreground: '#191919', cursorAccent: '#FAFAFA',
    selectionBackground: '#EEEEEE',
    black: '#191919', red: '#C22B33', green: '#14795F', yellow: '#6E5A0E',
    blue: '#1F5FA8', magenta: '#A62B5C', cyan: '#456277', white: '#525252',
    brightBlack: '#8C8C8C', brightRed: '#D14A50', brightGreen: '#1C9A7B', brightYellow: '#A85A12',
    brightBlue: '#3A7CC4', brightMagenta: '#C24A76', brightCyan: '#5E7E94', brightWhite: '#191919',
  },
  dark: {
    background: '#0B0B0B', foreground: '#F4F4F4', cursorAccent: '#0B0B0B',
    selectionBackground: '#232323',
    black: '#232323', red: '#FF5A5F', green: '#4FC0A0', yellow: '#E8D6A8',
    blue: '#6FA8E8', magenta: '#E884A8', cyan: '#8FADC2', white: '#A1A1A1',
    brightBlack: '#3A3A3A', brightRed: '#FF8A8E', brightGreen: '#7FD6BC', brightYellow: '#F2E6C6',
    brightBlue: '#9AC4F0', brightMagenta: '#F0AAC4', brightCyan: '#B2C8D6', brightWhite: '#F4F4F4',
  },
};

function studioChat(mode: 'dark' | 'light', cursor: string): TerminalColors {
  return { ...STUDIO_CHAT_BASE[mode], cursor };
}

/** CYANOTYPE — the blueprint print itself, inked. THE DEFAULT, named once. */
const STUDIO_CYANOTYPE: AppTheme = {
  id: 'studio-cyanotype',
  label: 'Cyanotype',
  mode: 'dark',
  styleId: 'studio',
  ui: studioUi('dark', { accent: '#5AA9F0', accent2: '#8CC6F7', accentDim: '#5AA9F02e', onaccent: '#06121F' }),
  chat: studioChat('dark', '#5AA9F0'),
};
const STUDIO_CYANOTYPE_LIGHT: AppTheme = {
  id: 'studio-cyanotype-light',
  label: 'Cyanotype',
  mode: 'light',
  styleId: 'studio',
  ui: studioUi('light', { accent: '#1D5FA6', accent2: '#164A85', accentDim: '#1D5FA624', onaccent: '#FFFFFF' }),
  chat: studioChat('light', '#1D5FA6'),
};

/** INDIGO DYE — vat-dyed indigo, the furthest a STUDIO primary goes toward
 * chroma. */
const STUDIO_INDIGO: AppTheme = {
  id: 'studio-indigo-dye',
  label: 'Indigo Dye',
  mode: 'dark',
  styleId: 'studio',
  ui: studioUi('dark', { accent: '#8B8AF5', accent2: '#B3B2FA', accentDim: '#8B8AF52e', onaccent: '#0B0A1F' }),
  chat: studioChat('dark', '#8B8AF5'),
};
const STUDIO_INDIGO_LIGHT: AppTheme = {
  id: 'studio-indigo-dye-light',
  label: 'Indigo Dye',
  mode: 'light',
  styleId: 'studio',
  ui: studioUi('light', { accent: '#4338CA', accent2: '#372FA6', accentDim: '#4338CA24', onaccent: '#FFFFFF' }),
  chat: studioChat('light', '#4338CA'),
};

/** SUMI — ink stick and nothing else, and the proof the system needs no
 * colour at all. Its primary is LITERALLY the `--ink` token in both modes, so
 * Sumi is the one slot whose entire palette is zero-chroma except the four
 * intents — maximum accent contrast in the programme (17.9), and
 * unmistakably deliberate rather than the unstyled-button read a mid-grey
 * primary would have given. Additive-vs-neutral is separated structurally,
 * not by hue: by luminance (2.53x dark, 7.96x light) and by the
 * fill-vs-badge material rule, which is exactly why that rule exists. */
const STUDIO_SUMI: AppTheme = {
  id: 'studio-sumi',
  label: 'Sumi',
  mode: 'dark',
  styleId: 'studio',
  ui: studioUi('dark', { accent: '#F4F4F4', accent2: '#FFFFFF', accentDim: '#F4F4F42e', onaccent: '#0B0B0B' }),
  chat: studioChat('dark', '#F4F4F4'),
};
const STUDIO_SUMI_LIGHT: AppTheme = {
  id: 'studio-sumi-light',
  label: 'Sumi',
  mode: 'light',
  styleId: 'studio',
  ui: studioUi('light', { accent: '#191919', accent2: '#000000', accentDim: '#19191914', onaccent: '#FAFAFA' }),
  chat: studioChat('light', '#191919'),
};

/** The ten ramp keys + the eight shared non-primary keys — the tokens that
 * MUST be byte-identical across the three themes in a mode. Exported so the
 * ground-stability gate reads the same list this module composes from, rather
 * than a second copy that could drift out of agreement with it. */
export const STUDIO_GROUND_KEYS: readonly (keyof UiTokens)[] = [
  'bg', 'bgDeep', 'surface', 'surface2', 'surface3', 'border', 'border2', 'ink', 'ink2', 'ink3',
  'sea', 'yellow', 'aux1', 'aux2', 'aux3', 'aux4', 'aux5', 'auxDim',
];

// QUARTERDECK — the three curated slots of the navigation-instrument style
// (PR-8).
//
// ACCENT DOCTRINE (cross-style ruling R4): the accent is always A METAL AT
// SOME STAGE OF OXIDATION — polished brass, verdigris, dark bronze. Brass is
// QUARTERDECK's exclusively; no other style may take a warm metal for an
// accent or a hairline.
//
// The two guardrails that stop "nautical" sliding into tavern kitsch, and
// they are both visible in the hex below:
//   1. COOL-NEUTRAL GROUNDS. Not one ground is sepia. Brass is the ONE warm
//      note in the style and it is reserved for the accent.
//   2. AGE IS CARRIED BY LINEWORK, NEVER BY STAIN. The light slot's ink is
//      lamp-black `#1C1D18` with no brown in it — the rhumb grids, the ruled
//      dividers and the engraved ticks do all the aging (styles.css).
//
// Every hex is transcribed from the approved spec (WF-B specs/quarterdeck.md
// §2), whose palettes were floor-verified before approval — 3 slots, 0
// failures, and scripts/themes-check.mjs measures them back to the spec's own
// numbers on every run.
// ---------------------------------------------------------------------

/** Tarred timber below decks, bone-ivory ink, polished brass instruments. */
const QUARTERDECK_DEAD_RECKONING: AppTheme = {
  id: 'quarterdeck-dead-reckoning',
  label: 'Dead Reckoning',
  mode: 'dark',
  styleId: 'quarterdeck',
  ui: {
    bg: '#0C0E0D', bgDeep: '#060809', surface: '#14181A', surface2: '#1B2124',
    surface3: '#232A2D', border: '#2B3336', border2: '#3C464A',
    ink: '#E7E1D0', ink2: '#A49C88', ink3: '#7A7361',
    accent: '#C99A45', accent2: '#E2B662', accentDim: '#C99A452e',
    sea: '#34707C', onaccent: '#1A1206', yellow: '#F2D9A0',
    aux1: '#7FA3B8', aux2: '#6BA88C', aux3: '#D2794A', aux4: '#D9BC7A', aux5: '#8C93D6', auxDim: '#7A7361',
  },
  chat: {
    background: '#0C0E0D', foreground: '#E7E1D0', cursor: '#C99A45', cursorAccent: '#0C0E0D',
    selectionBackground: '#232A2D',
    black: '#232A2D', red: '#FF6B52', green: '#6BA88C', yellow: '#F2D9A0',
    blue: '#7FA3B8', magenta: '#8C93D6', cyan: '#5A9AA6', white: '#A49C88',
    brightBlack: '#3C464A', brightRed: '#FF9482', brightGreen: '#93C4AE', brightYellow: '#F8E8C4',
    brightBlue: '#A6C2D2', brightMagenta: '#AEB3E2', brightCyan: '#86BCC6', brightWhite: '#E7E1D0',
  },
};

/** The admiralty office in weather — colder, harder, barometric. Two watches
 * on the same ship, not two products: same structure as Dead Reckoning with a
 * colder cast throughout. Its metal is VERDIGRIS (hue ~163°, a blue-green
 * patina — a 20°+ band split from Destroyer's yellow-green running light,
 * ruling R6). A pale-STEEL accent was rejected on the way here: neutral *is*
 * steel in this style, so a grey accent would make the one authorized
 * additive action indistinguishable from every inert control. */
const QUARTERDECK_STORM_GLASS: AppTheme = {
  id: 'quarterdeck-storm-glass',
  label: 'Storm Glass',
  mode: 'dark',
  styleId: 'quarterdeck',
  ui: {
    bg: '#0A0D12', bgDeep: '#05070A', surface: '#121820', surface2: '#182130',
    surface3: '#1F2A3A', border: '#232F3D', border2: '#35455A',
    ink: '#DFE6EA', ink2: '#8D9AA8', ink3: '#69757F',
    accent: '#63B8A0', accent2: '#8FD4C0', accentDim: '#63B8A02e',
    sea: '#4C86A8', onaccent: '#04150F', yellow: '#E8D6A0',
    aux1: '#7FA8D6', aux2: '#6BB89C', aux3: '#D68A5C', aux4: '#C9A96B', aux5: '#9A9AD6', auxDim: '#69757F',
  },
  chat: {
    background: '#0A0D12', foreground: '#DFE6EA', cursor: '#63B8A0', cursorAccent: '#0A0D12',
    selectionBackground: '#1F2A3A',
    black: '#1F2A3A', red: '#FF6B52', green: '#6BB89C', yellow: '#E8D6A0',
    blue: '#7FA8D6', magenta: '#9A9AD6', cyan: '#63B8A0', white: '#8D9AA8',
    brightBlack: '#35455A', brightRed: '#FF9482', brightGreen: '#93CFB8', brightYellow: '#F2E6C4',
    brightBlue: '#A6C4E4', brightMagenta: '#BABAE4', brightCyan: '#8FD4C0', brightWhite: '#DFE6EA',
  },
};

/** Cartridge paper and lamp-black ink at dawn — NOT sepia parchment. The
 * ground is a cool cream, the ink is near-black with no brown in it, and the
 * metal is dark bronze. `#4F3A18` at 9.2:1 on the ground is deliberately
 * sombre: it is what buys separation from the rust destructive on a light
 * ground, and the documented fallback if it reads too dark is to move the
 * metal to verdigris — never to lighten the bronze, which collapses the
 * additive/destructive gap (spec §9). */
const QUARTERDECK_CHART_LIGHT: AppTheme = {
  id: 'quarterdeck-chart-light',
  label: 'Chart Light',
  mode: 'light',
  styleId: 'quarterdeck',
  ui: {
    bg: '#EFEEE6', bgDeep: '#E4E2D6', surface: '#F9F8F2', surface2: '#F2F0E6',
    surface3: '#E9E7DA', border: '#D4D0BF', border2: '#B6B1A0',
    ink: '#1C1D18', ink2: '#565547', ink3: '#7E7C6C',
    accent: '#4F3A18', accent2: '#3A2A10', accentDim: '#4F3A1824',
    sea: '#205B66', onaccent: '#FBFAF4', yellow: '#6E5A12',
    aux1: '#2A5A7A', aux2: '#2F6B54', aux3: '#9A4A22', aux4: '#5A4A8A', aux5: '#8A2F52', auxDim: '#7E7C6C',
  },
  chat: {
    background: '#EFEEE6', foreground: '#1C1D18', cursor: '#4F3A18', cursorAccent: '#EFEEE6',
    selectionBackground: '#E9E7DA',
    black: '#1C1D18', red: '#A32E23', green: '#2F6B54', yellow: '#6E5A12',
    blue: '#2A5A7A', magenta: '#8A2F52', cyan: '#205B66', white: '#565547',
    brightBlack: '#7E7C6C', brightRed: '#B8463A', brightGreen: '#3D8268', brightYellow: '#87701C',
    brightBlue: '#3A6E90', brightMagenta: '#A34168', brightCyan: '#2C707C', brightWhite: '#1C1D18',
  },
};

// ---------------------------------------------------------------------
// STARSHIP — the three curated slots of the disciplined deck (PR-6).
//
// Two dark, one light, and the pair of darks is the point: Void Watch is the
// reference bridge, Deep Field is the SAME bridge far from any star. Deep
// Field de-lights by dropping LUMINANCE AND CHROMA toward achromatic and
// never by hue-shifting to amber or red — red-light discipline is
// Destroyer's exclusively (rulings R1 and R5), and an amber ground would
// make the most safety-critical colour in the product ambient.
//
// The accent band is cyan (hue ~187 in Void Watch), which ruling R17 assigns
// to STARSHIP alone: measured, every Synthwave attempt put its accent within
// 1-8 degrees of it, and Glass Tide Pool's green-teal sits at ~170 on a
// green-black rather than a navy ground.
//
// Every hex below is transcribed from the approved spec (WF-B
// specs/starship.md, section 2), whose palettes were floor-verified against
// scripts/themes-check.mjs before approval — 3 slots, 0 failures.
// ---------------------------------------------------------------------

/** Deep-space navy under a cyan-teal HUD line — the reference STARSHIP look
 * and the style's dark default. */
const STARSHIP_VOID_WATCH: AppTheme = {
  id: 'starship-void-watch',
  label: 'Void Watch',
  mode: 'dark',
  styleId: 'starship',
  ui: {
    bg: '#060A12', bgDeep: '#03050A', surface: '#0D1420', surface2: '#121C2C',
    surface3: '#172438', border: '#1C2B40', border2: '#29405C',
    ink: '#E6EDF6', ink2: '#8EA3BD', ink3: '#5B7291',
    accent: '#34C3D6', accent2: '#74E4EF', accentDim: '#34C3D62e',
    sea: '#4C8FE8', onaccent: '#04141A', yellow: '#F7DC9E',
    aux1: '#4C8FE8', aux2: '#48D6A0', aux3: '#FF9A5C', aux4: '#B48CFF', aux5: '#F27FA8', auxDim: '#5B7291',
  },
  chat: {
    background: '#060A12', foreground: '#E6EDF6', cursor: '#34C3D6', cursorAccent: '#060A12',
    selectionBackground: '#172438',
    black: '#172438', red: '#FF6B72', green: '#48D6A0', yellow: '#F7DC9E',
    blue: '#4C8FE8', magenta: '#B48CFF', cyan: '#34C3D6', white: '#8EA3BD',
    brightBlack: '#29405C', brightRed: '#FF9AA0', brightGreen: '#7FE7C2', brightYellow: '#FBECC6',
    brightBlue: '#86B6F0', brightMagenta: '#CDB1FF', brightCyan: '#74E4EF', brightWhite: '#E6EDF6',
  },
};

/** The ship far from any star — STARSHIP's dark-room watch. Surfaces lose
 * their navy cast entirely and the accent cools to a pale ice blue; the
 * starfield is this theme's identity, and the painter reads the ground's own
 * luminance to raise the field's density and alpha as the ground darkens. */
const STARSHIP_DEEP_FIELD: AppTheme = {
  id: 'starship-deep-field',
  label: 'Deep Field',
  mode: 'dark',
  styleId: 'starship',
  ui: {
    bg: '#020304', bgDeep: '#000000', surface: '#0A0C0E', surface2: '#111417',
    surface3: '#181C20', border: '#22272C', border2: '#333A41',
    ink: '#E8EDF2', ink2: '#96A1AC', ink3: '#6B757F',
    accent: '#8FD4FF', accent2: '#C2E9FF', accentDim: '#8FD4FF2e',
    sea: '#7FA8D8', onaccent: '#04121C', yellow: '#E8D6A8',
    aux1: '#7FB4E8', aux2: '#6FD8B4', aux3: '#E89A6B', aux4: '#B4A0E8', aux5: '#E88FA8', auxDim: '#6B757F',
  },
  chat: {
    background: '#020304', foreground: '#E8EDF2', cursor: '#8FD4FF', cursorAccent: '#020304',
    selectionBackground: '#181C20',
    black: '#181C20', red: '#FF7A83', green: '#6FD8B4', yellow: '#E8D6A8',
    blue: '#7FB4E8', magenta: '#B4A0E8', cyan: '#8FD4FF', white: '#96A1AC',
    brightBlack: '#333A41', brightRed: '#FFA3A9', brightGreen: '#9AE6CC', brightYellow: '#F2E6C8',
    brightBlue: '#A8CCF0', brightMagenta: '#CFC1F0', brightCyan: '#C2E9FF', brightWhite: '#E8EDF2',
  },
};

/** A lit bridge has no visible stars: this slot mounts no field at all (the
 * painter paints nothing and the canvas is torn down — see starship-stars.ts
 * syncStarfieldGround). It keeps the hairline engineering-grid wash instead. */
const STARSHIP_DAY_WATCH: AppTheme = {
  id: 'starship-day-watch',
  label: 'Day Watch',
  mode: 'light',
  styleId: 'starship',
  ui: {
    bg: '#EEF1F6', bgDeep: '#E1E7EF', surface: '#FFFFFF', surface2: '#F4F7FB',
    surface3: '#EAEFF5', border: '#D6DEE8', border2: '#BFCAD9',
    ink: '#101827', ink2: '#46536A', ink3: '#79869A',
    accent: '#0B7E8C', accent2: '#0A6C79', accentDim: '#0B7E8C24',
    sea: '#2159C9', onaccent: '#FFFFFF', yellow: '#6F5A0E',
    aux1: '#2159C9', aux2: '#0F7A56', aux3: '#A2500F', aux4: '#6A3FBF', aux5: '#B02E6A', auxDim: '#79869A',
  },
  chat: {
    background: '#EEF1F6', foreground: '#101827', cursor: '#0B7E8C', cursorAccent: '#EEF1F6',
    selectionBackground: '#EAEFF5',
    black: '#101827', red: '#C0303C', green: '#0F7A56', yellow: '#A2500F',
    blue: '#2159C9', magenta: '#6A3FBF', cyan: '#0B7E8C', white: '#46536A',
    brightBlack: '#79869A', brightRed: '#D2515C', brightGreen: '#158F66', brightYellow: '#8A7315',
    brightBlue: '#3A6FD8', brightMagenta: '#7F55CF', brightCyan: '#0A6C79', brightWhite: '#101827',
  },
};

// MAINFRAME '84 — the four curated slots of the phosphor style (PR-9).
//
// Three dark, one light, and the light one is a PRINTOUT rather than an
// inverted screen: Paper White's `surface-2` IS the greenbar band, so
// alternating table rows and nested panels literally render the mint stripe
// of continuous-feed ledger paper.
//
// THE CHROME IS MONOCHROME; THE `aux` LADDER IS THE TERMINAL'S OWN COLOUR
// TABLE. A near-monochrome theme still owes five differentiated chart hues
// (themes-check gates aux1-5 at >= 3.0 against both grounds). Rather than
// re-pin them to a luminance ramp — another style's move — MAINFRAME maps
// them onto the ANSI bright-8 set, which is what a real DEC VT241 or IBM
// 3279 did: the field was one phosphor, the colour table was separate. Aux
// hues appear in charts and in code-token rendering only, never in chrome.
//
// R6 NARROWED, and greppable: MAINFRAME is the ONE style licensed to ship a
// CHROMATIC INK STACK. Measured raw chroma of `ink`: Green 23.9, Amber 39.6,
// DEC 22.4 — against <= 9.0 for every other dark slot in the programme. In
// Destroyer the body text is grey and one lamp is green; here the body text
// IS the green. scripts/style-intent.mjs arm 5 enforces both halves of that
// rule with a positive control.
//
// Every hex below is transcribed from the approved spec (WF-B
// specs/mainframe.md §2), floor-verified against scripts/themes-check.mjs
// (4 slots, 0 failures) AND against the intent gate (4 slots / 24 pairs,
// 0 failures) before approval.
// ---------------------------------------------------------------------

/** DEC VT220 P1 phosphor — the flagship, and MAINFRAME's dark default.
 * `--sea #57C4D8` is the COLD MAINTENANCE LIGHT: the one relief hue in a
 * warm-monochrome field (graft from mainframe-2). */
const MAINFRAME_GREEN: AppTheme = {
  id: 'mainframe-green-phosphor',
  label: 'Green Phosphor',
  mode: 'dark',
  styleId: 'mainframe',
  ui: {
    bg: '#061008', bgDeep: '#030A05', surface: '#0B1A0F', surface2: '#0F2214',
    surface3: '#142B1A', border: '#1D3D26', border2: '#2C5A38',
    ink: '#B9F6C4', ink2: '#74C489', ink3: '#4E8A5F',
    accent: '#46E077', accent2: '#7BF0A0', accentDim: '#46E0772e',
    sea: '#57C4D8', onaccent: '#04160A', yellow: '#FFD24A',
    aux1: '#FF7B72', aux2: '#7EE787', aux3: '#FFD24A', aux4: '#79C0FF', aux5: '#D2A8FF', auxDim: '#4E8A5F',
  },
  chat: {
    background: '#061008', foreground: '#B9F6C4', cursor: '#46E077', cursorAccent: '#061008',
    selectionBackground: '#142B1A',
    black: '#142B1A', red: '#FF7B72', green: '#7EE787', yellow: '#FFD24A',
    blue: '#79C0FF', magenta: '#D2A8FF', cyan: '#57C4D8', white: '#74C489',
    brightBlack: '#2C5A38', brightRed: '#FFA198', brightGreen: '#A5F3B4', brightYellow: '#FFE08A',
    brightBlue: '#A5D6FF', brightMagenta: '#E2C5FF', brightCyan: '#8AD9E8', brightWhite: '#B9F6C4',
  },
};

/** IBM 3278 / Wyse amber, kept matte. THIS THEME PINS THE STYLE'S
 * DESTRUCTIVE VALUE (R5 narrowed): amber is the field here, not a signal, so
 * what must be protected is the destructive affordance — and the surface
 * ladder is deliberately the darkest in the style because the intent gate
 * refused the first one at 2.85 for `--ink-3` against `surface-3`. The ink
 * is a light amber at 40pp saturation, not the 96pp of the accent; that
 * saturation gap is what lets the destructive be told apart from body text.
 * `--yellow` is DOCUMENTED-DEGENERATE here (amber on amber) and attention
 * text is carried by reverse video instead — naming the degeneracy is the
 * point, it is the token no gate can catch. */
const MAINFRAME_AMBER: AppTheme = {
  id: 'mainframe-amber-phosphor',
  label: 'Amber Phosphor',
  mode: 'dark',
  styleId: 'mainframe',
  ui: {
    bg: '#0F0A02', bgDeep: '#080501', surface: '#191105', surface2: '#201607',
    surface3: '#281C09', border: '#45300F', border2: '#63461A',
    ink: '#FFD79A', ink2: '#D9A85C', ink3: '#85632F',
    accent: '#FFB000', accent2: '#FFC94A', accentDim: '#FFB0002e',
    sea: '#6FC8DC', onaccent: '#1A1000', yellow: '#FFDF7E',
    aux1: '#FF8A7A', aux2: '#8FD98F', aux3: '#FFC94A', aux4: '#7FC8E8', aux5: '#D6A8E0', auxDim: '#85632F',
  },
  chat: {
    background: '#0F0A02', foreground: '#FFD79A', cursor: '#FFB000', cursorAccent: '#0F0A02',
    selectionBackground: '#281C09',
    black: '#281C09', red: '#FF8A7A', green: '#8FD98F', yellow: '#FFC94A',
    blue: '#7FC8E8', magenta: '#D6A8E0', cyan: '#6FC8DC', white: '#D9A85C',
    brightBlack: '#63461A', brightRed: '#FFAFA2', brightGreen: '#B4E6B4', brightYellow: '#FFDF7E',
    brightBlue: '#A6DCF0', brightMagenta: '#E7C6ED', brightCyan: '#96DCEA', brightWhite: '#FFD79A',
  },
};

/** VT320 blue-white phosphor — the cold theme, so its maintenance light is
 * WARM mint (`--sea #6FE0C8`). Historically true, and it gives every
 * near-monochrome slot exactly one relief hue. */
const MAINFRAME_DEC_BLUE: AppTheme = {
  id: 'mainframe-dec-blue',
  label: 'DEC Blue',
  mode: 'dark',
  styleId: 'mainframe',
  ui: {
    bg: '#060A14', bgDeep: '#03050C', surface: '#0C1424', surface2: '#101B2E',
    surface3: '#16233A', border: '#1E3050', border2: '#2D4670',
    ink: '#C6DEFF', ink2: '#8FB2E0', ink3: '#56769E',
    accent: '#4FA8FF', accent2: '#8CC8FF', accentDim: '#4FA8FF2e',
    sea: '#6FE0C8', onaccent: '#04101F', yellow: '#FFD24A',
    aux1: '#FF8A8A', aux2: '#7EE7B0', aux3: '#FFC46B', aux4: '#79C0FF', aux5: '#D2A8FF', auxDim: '#56769E',
  },
  chat: {
    background: '#060A14', foreground: '#C6DEFF', cursor: '#4FA8FF', cursorAccent: '#060A14',
    selectionBackground: '#16233A',
    black: '#16233A', red: '#FF8A8A', green: '#7EE7B0', yellow: '#FFC46B',
    blue: '#79C0FF', magenta: '#D2A8FF', cyan: '#6FE0C8', white: '#8FB2E0',
    brightBlack: '#2D4670', brightRed: '#FFB0B0', brightGreen: '#A8F0CA', brightYellow: '#FFD79B',
    brightBlue: '#A5D6FF', brightMagenta: '#E2C5FF', brightCyan: '#9CEBDA', brightWhite: '#C6DEFF',
  },
};

/** The owner-named paper-white slot, built as LINE-PRINTER OUTPUT rather
 * than an inverted screen. The ground is cool paper (raw chroma 1.2 —
 * measurably neither Blueprint's warm cream nor another style's cartridge
 * cream), the ink is impact-ribbon near-black (chroma 2.4, so the R6
 * chromatic-ink clause does not apply to it), and `--surface-2` IS THE
 * GREENBAR BAND. The accent is printer green at hue 111, moved deliberately
 * out of Destroyer's 130-145 band (28° clear) and off Blueprint light's
 * olive at 93 (18° clear). Paper White glows at nothing and scanlines at
 * nothing — §4's raster layer is dark-slot only. */
const MAINFRAME_PAPER_WHITE: AppTheme = {
  id: 'mainframe-paper-white',
  label: 'Paper White',
  mode: 'light',
  styleId: 'mainframe',
  ui: {
    bg: '#F3F4F1', bgDeep: '#E6E8E3', surface: '#FBFCFA', surface2: '#E3EDE0',
    surface3: '#D8E4D5', border: '#C6C9C2', border2: '#A5A99F',
    ink: '#1F241E', ink2: '#4C5449', ink3: '#757D70',
    accent: '#2F6B24', accent2: '#245219', accentDim: '#2F6B2424',
    sea: '#1F5F8F', onaccent: '#FBFCFA', yellow: '#7A5E0A',
    aux1: '#A3341F', aux2: '#2F6B24', aux3: '#8A5A0C', aux4: '#1F5F8F', aux5: '#6B3A8A', auxDim: '#757D70',
  },
  chat: {
    background: '#F3F4F1', foreground: '#1F241E', cursor: '#2F6B24', cursorAccent: '#F3F4F1',
    selectionBackground: '#D8E4D5',
    black: '#1F241E', red: '#A3341F', green: '#2F6B24', yellow: '#8A5A0C',
    blue: '#1F5F8F', magenta: '#6B3A8A', cyan: '#0F6E7A', white: '#4C5449',
    brightBlack: '#757D70', brightRed: '#B94427', brightGreen: '#3C7F2E', brightYellow: '#9C6A12',
    brightBlue: '#2A6FA0', brightMagenta: '#7B4A9A', brightCyan: '#16808E', brightWhite: '#1F241E',
  },
};

// ---------------------------------------------------------------------
// DESTROYER — the three curated slots of the second non-identity style
// (PR-7). Two dark, one light.
//
// Every hex below is transcribed from the approved spec (WF-B
// specs/destroyer.md §2), whose palettes were floor-verified against
// scripts/themes-check.mjs before approval — 3 slots, 0 failures.
// `chat.background := ui.bg` in all three, so the aux floors are measured
// against ONE ground rather than two that could disagree.
// ---------------------------------------------------------------------

/**
 * BATTLE LANTERNS' `overrides` — the gate hole, closed. (spec §2.4)
 *
 * `themes-check.mjs` measures the 22 UI tokens only. The `--s-*` ladder,
 * `--c1..--c7`, `--d-*`, `--dom-*` and `--p-*` are MODE-SHARED: they were
 * tuned once against a slate ground and every theme inherits them. On
 * `#170707` several of those rungs degrade **below the reach of the gate**,
 * so the failure would ship silently — which is precisely what
 * `OVERRIDABLE_TOKENS` exists for. "v1 themes ship none" is a default, not a
 * law, and this is the first theme in the registry to spend it.
 *
 * THE RAMP. Seven rungs, hue held at 14 degrees, each separated by >= 1.32x
 * raw luminance, every rung >= 3.0 against ALL FOUR grounds (bg, surface,
 * surface-2, surface-3) — measured, and re-measured on every run by the
 * ramp arm of the smoke:
 *
 *   r1 #F9E9E4  16.64 / 15.39 / 14.42 / 13.16      (most urgent, brightest)
 *   r2 #F1C8BB  12.82 / 11.86 / 11.12 / 10.14      step 1.32
 *   r3 #ECA894   9.90 /  9.16 /  8.58 /  7.83      step 1.33
 *   r4 #E88A6D   7.75 /  7.17 /  6.71 /  6.13      step 1.32
 *   r5 #E66A45   6.09 /  5.63 /  5.28 /  4.81      step 1.32
 *   r6 #E24819   4.84 /  4.47 /  4.19 /  3.83      step 1.32
 *   r7 #C93D13   3.89 /  3.60 /  3.37 /  3.08      step 1.32  (least urgent)
 *
 * THE ASSIGNMENT, and what it costs. `--c1..--c7` take r1..r7 directly. The
 * `--s-*` rungs take the ramp position closest to their current LUMINANCE
 * RANK — nineteen tokens carrying twelve distinct hues collapse onto seven
 * rungs, and that is by construction rather than by accident: under a red
 * cast the eye reads brightness, so the ladder encodes RANK and §6's MIL-STD
 * silhouette carries identity. `--d-*` (seven) takes r1..r7 and `--dom-*`
 * (five, plus their soft `-bg` twins at the accent-dim alpha) takes r2..r6.
 *
 * ONE ADDITION TO THE SPEC, NAMED. §2.4 enumerates four mode-shared sets and
 * `--p-*` (the three planes) is a fifth with the identical failure mode —
 * control blue, exec green and storage violet on a red-night field. It is
 * re-pinned on the same ramp (r2/r4/r6) and measured by the same arm.
 * `--yellow-fill` and the `--sc-*` chips are deliberately NOT re-pinned:
 * `--yellow-fill` is the invariant attention fill that brand.ts paints into
 * the tab badge, and an attention signal that changes colour with the theme
 * dropdown is not an attention signal.
 */
const BATTLE_LANTERNS_RAMP: Partial<Record<OverridableToken, string>> = {
  // --s-* : the status ladder, by luminance rank
  '--s-decided': '#F1C8BB',
  '--s-proven': '#ECA894',
  '--s-theory': '#E88A6D',
  '--s-open': '#E66A45',
  '--s-risk': '#E24819',
  '--s-built': '#F1C8BB',
  '--s-stub': '#C93D13',
  '--s-future': '#F9E9E4',
  '--s-scoping': '#E24819',
  '--s-scoped': '#E24819',
  '--s-planning': '#E66A45',
  '--s-planned': '#F9E9E4',
  '--s-implementing': '#E88A6D',
  '--s-implemented': '#ECA894',
  '--s-re-implementing': '#E88A6D',
  '--s-testing': '#E66A45',
  '--s-tested': '#ECA894',
  '--s-completed': '#F1C8BB',
  '--s-aborted': '#C93D13',
  // --c1..--c7 : the categorical series, r1..r7 in rank order
  '--c1': '#F9E9E4',
  '--c2': '#F1C8BB',
  '--c3': '#ECA894',
  '--c4': '#E88A6D',
  '--c5': '#E66A45',
  '--c6': '#E24819',
  '--c7': '#C93D13',
  // --d-* : the seven metamodel domains, r1..r7 in existing order
  '--d-schema': '#F9E9E4',
  '--d-process': '#F1C8BB',
  '--d-org': '#ECA894',
  '--d-fn': '#E88A6D',
  '--d-knowledge': '#E66A45',
  '--d-eval': '#E24819',
  '--d-tools': '#C93D13',
  // --dom-* : the five engineer domains, r2..r6, plus the soft chip twins
  '--dom-frontend': '#F1C8BB',
  '--dom-backend': '#ECA894',
  '--dom-database': '#E88A6D',
  '--dom-devops': '#E66A45',
  '--dom-harness': '#E24819',
  '--dom-frontend-bg': '#f1c8bb2e',
  '--dom-backend-bg': '#eca8942e',
  '--dom-database-bg': '#e88a6d2e',
  '--dom-devops-bg': '#e66a452e',
  '--dom-harness-bg': '#e248192e',
  // --p-* : the three planes (the fifth mode-shared set; see the note above)
  '--p-control': '#F1C8BB',
  '--p-exec': '#E88A6D',
  '--p-storage': '#E24819',
};

/** Gunmetal blue-black hull steel — the reference DESTROYER look and its
 * default dark slot. Additive is running-light green (hue 137), which this
 * style may take because its ink is neutral: chroma(ink) 2.4, the greppable
 * half of the narrowed ruling R6 (Mainframe owns green as the FIELD, this
 * style owns it as an instrument lamp ON a field). */
const DESTROYER_BLACKOUT: AppTheme = {
  id: 'destroyer-blackout',
  label: 'Blackout',
  mode: 'dark',
  styleId: 'destroyer',
  ui: {
    bg: '#12161B', bgDeep: '#0A0D10', surface: '#1A2029', surface2: '#212831',
    surface3: '#29313C', border: '#333D49', border2: '#46525F',
    ink: '#E6E9EC', ink2: '#9AA5B1', ink3: '#717D8A',
    accent: '#4FB06B', accent2: '#79D492', accentDim: '#4FB06B2e',
    sea: '#6E93B8', onaccent: '#08140C', yellow: '#DFC98F',
    aux1: '#6E93B8', aux2: '#5FBFA0', aux3: '#E0703A', aux4: '#9A8CD6', aux5: '#D67A94', auxDim: '#717D8A',
  },
  chat: {
    background: '#12161B', foreground: '#E6E9EC', cursor: '#4FB06B', cursorAccent: '#12161B',
    selectionBackground: '#29313C',
    black: '#29313C', red: '#E4695B', green: '#4FB06B', yellow: '#DFC98F',
    blue: '#6E93B8', magenta: '#D67A94', cyan: '#5FBFA0', white: '#9AA5B1',
    brightBlack: '#46525F', brightRed: '#F08A7E', brightGreen: '#79D492', brightYellow: '#EBDCB4',
    brightBlue: '#93B2CE', brightMagenta: '#E39FB3', brightCyan: '#86D4BE', brightWhite: '#E6E9EC',
  },
};

/**
 * THE RED-NIGHT THEME, and the programme's only one (ruling R1). Real USN
 * night-steaming doctrine: interior lighting shifts to red-only to preserve
 * rod-cell night vision, so everything lives in the red-orange band ON
 * PURPOSE.
 *
 * Additive is separated from destructive by VALUE, not hue — under a red cast
 * the eye reads brightness — so the additive slot takes the palest,
 * highest-luminance warm tone (a lit filament) while destructive sits
 * mid-luminance and error is the only saturated deep tone.
 *
 * It is assigned to this style and to nothing else because a monochrome-red
 * field can only keep four intents legible if SHAPE is already the primary
 * channel, and DESTROYER's MIL-STD silhouettes plus stencil bridges are the
 * only icon system in the programme that qualifies. That is also why it is
 * the strongest CVD case in the programme rather than its weakest.
 */
const DESTROYER_BATTLE_LANTERNS: AppTheme = {
  id: 'destroyer-battle-lanterns',
  label: 'Battle Lanterns',
  mode: 'dark',
  styleId: 'destroyer',
  ui: {
    bg: '#170707', bgDeep: '#0C0303', surface: '#241010', surface2: '#2E1515',
    surface3: '#3A1B1B', border: '#4E2323', border2: '#6B3230',
    ink: '#F5DCD8', ink2: '#C79A94', ink3: '#9A716C',
    accent: '#FFC2A0', accent2: '#FFE0CC', accentDim: '#FFC2A02e',
    sea: '#E0A8A0', onaccent: '#2A0C06', yellow: '#FFD9A8',
    aux1: '#FFB08C', aux2: '#E8C0A0', aux3: '#FF6B57', aux4: '#D69A9A', aux5: '#FFD1A8', auxDim: '#9A716C',
  },
  chat: {
    background: '#170707', foreground: '#F5DCD8', cursor: '#FFC2A0', cursorAccent: '#170707',
    selectionBackground: '#3A1B1B',
    black: '#3A1B1B', red: '#FF6B57', green: '#E8C0A0', yellow: '#FFD9A8',
    blue: '#E0A8A0', magenta: '#D69A9A', cyan: '#FFB08C', white: '#C79A94',
    brightBlack: '#6B3230', brightRed: '#FF9080', brightGreen: '#F2D5BE', brightYellow: '#FFE6C6',
    brightBlue: '#EDC4BE', brightMagenta: '#E4B8B8', brightCyan: '#FFC9AE', brightWhite: '#F5DCD8',
  },
  overrides: BATTLE_LANTERNS_RAMP,
};

/** Topside daylight steel with an overcast-sea gray-green cast — the only
 * light slot DESTROYER ships. Its accent contrast is 3.86, above the 3.0 gate
 * and the tightest in the style; it is never used for body text, and
 * `accent2 #215E34` is the value to reach for when text-on-ground is wanted
 * (spec §9). */
const DESTROYER_HAZE_GRAY: AppTheme = {
  id: 'destroyer-haze-gray',
  label: 'Haze Gray',
  mode: 'light',
  styleId: 'destroyer',
  ui: {
    bg: '#DFE1DE', bgDeep: '#CFD2CE', surface: '#ECEEEC', surface2: '#F4F5F3',
    surface3: '#E4E7E3', border: '#B8BCB8', border2: '#9BA09B',
    ink: '#1B1F22', ink2: '#454C52', ink3: '#6E767C',
    accent: '#2E7D46', accent2: '#215E34', accentDim: '#2E7D4624',
    sea: '#2C5F86', onaccent: '#FFFFFF', yellow: '#6E5A0E',
    aux1: '#2C5F86', aux2: '#1F6B5A', aux3: '#A34A1F', aux4: '#5B3FA8', aux5: '#A02C55', auxDim: '#6E767C',
  },
  chat: {
    background: '#DFE1DE', foreground: '#1B1F22', cursor: '#2E7D46', cursorAccent: '#DFE1DE',
    selectionBackground: '#CFD2CE',
    black: '#1B1F22', red: '#A02C55', green: '#1F6B5A', yellow: '#6E5A0E',
    blue: '#2C5F86', magenta: '#5B3FA8', cyan: '#0F6E8C', white: '#454C52',
    brightBlack: '#6E767C', brightRed: '#B53A65', brightGreen: '#2A7E6B', brightYellow: '#86700F',
    brightBlue: '#38729B', brightMagenta: '#6C4EBC', brightCyan: '#1782A2', brightWhite: '#1B1F22',
  },
};

// ---------------------------------------------------------------------
// Registry + resolution (mirrors the retired terminal-presets.ts API shape)
// ---------------------------------------------------------------------

/** Dropdown order per mode (themesForMode preserves array order): built-in
 * default first, then alphabetical. */
export const THEMES: AppTheme[] = [
  MISSION_CONTROL,
  CATPPUCCIN_MOCHA,
  DRACULA,
  EVERFOREST_DARK,
  GRUVBOX_DARK,
  KANAGAWA_WAVE,
  NORD,
  ROSE_PINE,
  SOLARIZED_DARK,
  TOKYO_NIGHT,
  CHART_TABLE,
  CATPPUCCIN_LATTE,
  EVERFOREST_LIGHT,
  FLEXOKI_LIGHT,
  GITHUB_LIGHT,
  GRUVBOX_LIGHT,
  KANAGAWA_LOTUS,
  ROSE_PINE_DAWN,
  SOLARIZED_LIGHT,
  TOKYO_NIGHT_DAY,
  // ---- GLASS (PR-5) — invisible while the painted style is Blueprint ----
  GLASS_SPECTRAL,
  GLASS_TIDE_POOL,
  GLASS_ANILINE,
  GLASS_SPECTRAL_DAY,
  // ---- SYNTHWAVE '88 (PR-11) — three DARK slots, no light one (R19) ----
  SYNTHWAVE_NIGHT_DRIVE,
  SYNTHWAVE_ULTRAVIOLET,
  SYNTHWAVE_VECTOR,
  // ---- QUARTERDECK (PR-8) — invisible while the painted style is Blueprint ----
  QUARTERDECK_DEAD_RECKONING,
  QUARTERDECK_STORM_GLASS,
  QUARTERDECK_CHART_LIGHT,
  // ---- STARSHIP (PR-6) — invisible while the painted style is Blueprint ----
  STARSHIP_VOID_WATCH,
  STARSHIP_DEEP_FIELD,
  STARSHIP_DAY_WATCH,
  // ---- STUDIO (PR-10) — invisible while the painted style is Blueprint ----
  STUDIO_CYANOTYPE,
  STUDIO_INDIGO,
  STUDIO_SUMI,
  STUDIO_CYANOTYPE_LIGHT,
  STUDIO_INDIGO_LIGHT,
  STUDIO_SUMI_LIGHT,
  // ---- MAINFRAME '84 (PR-9) — invisible while the painted style is not it -
  MAINFRAME_GREEN,
  MAINFRAME_AMBER,
  MAINFRAME_DEC_BLUE,
  MAINFRAME_PAPER_WHITE,
  // ---- DESTROYER (PR-7) — same: styleId scopes them out of every list ----
  DESTROYER_BLACKOUT,
  DESTROYER_BATTLE_LANTERNS,
  DESTROYER_HAZE_GRAY,
];

export const DEFAULT_THEME_DARK = 'mission-control';
export const DEFAULT_THEME_LIGHT = 'chart-table';

/** Per-style mode defaults. A style's slots are its own, so its default must
 * be too: falling back to Mission Control while GLASS is painted would put a
 * Blueprint palette under GLASS's materials. Keyed by style id; a style with
 * no entry inherits the identity style's pair, which is what makes this
 * table additive. */
const IDENTITY_DEFAULTS = { dark: DEFAULT_THEME_DARK, light: DEFAULT_THEME_LIGHT };
const STYLE_DEFAULTS: Record<string, { dark: string; light: string }> = {
  [IDENTITY_STYLE_ID]: IDENTITY_DEFAULTS,
  glass: { dark: 'glass-spectral', light: 'glass-spectral-day' },
  // SYNTHWAVE registers no light slot (R19). The light entry therefore names
  // the IDENTITY style's light default explicitly rather than inheriting it
  // by accident: light mode on SYNTHWAVE paints Chart Table, the Dark/Light
  // toggle keeps working, and the Settings light slot says so in an info line
  // instead of a disabled control. Pointing it at the style's own DARK theme
  // would be exactly the "redefine what the global control means" R19
  // refuses, and leaving it unset would make the fallback implicit.
  synthwave: { dark: 'synthwave-night-drive', light: DEFAULT_THEME_LIGHT },
  quarterdeck: { dark: 'quarterdeck-dead-reckoning', light: 'quarterdeck-chart-light' },
  starship: { dark: 'starship-void-watch', light: 'starship-day-watch' },
  // ONE default, named ONCE: the dropdown opens on Cyanotype in both modes.
  studio: { dark: 'studio-cyanotype', light: 'studio-cyanotype-light' },
  mainframe: { dark: 'mainframe-green-phosphor', light: 'mainframe-paper-white' },
  destroyer: { dark: 'destroyer-blackout', light: 'destroyer-haze-gray' },
};

const byId = new Map(THEMES.map((t) => [t.id, t]));

/** The style a theme belongs to. Absent `styleId` ⇒ the identity style, so
 * the twenty pre-existing themes need no edit and cannot change behaviour. */
export function themeStyleId(t: AppTheme): string {
  return t.styleId ?? IDENTITY_STYLE_ID;
}

/** The default theme id for one style × mode. Falls back to the identity
 * style's default for a style that curated none — never to nothing. */
export function defaultThemeFor(styleId: string, mode: 'dark' | 'light'): string {
  // Not `?? STYLE_DEFAULTS[IDENTITY_STYLE_ID]`: an INDEX into a Record is
  // `T | undefined` under the console client's strictness, so the fallback
  // has to be the object itself, not another lookup.
  const pair = STYLE_DEFAULTS[styleId] ?? IDENTITY_DEFAULTS;
  return mode === 'light' ? pair.light : pair.dark;
}

/**
 * The themes offered in one mode's dropdown, for the style being painted.
 *
 * `styleId` defaults to the identity style ON PURPOSE: every pre-existing
 * caller keeps the exact list it had, so a style's curated slots cannot leak
 * into a Blueprint session. A style that registers no light theme simply
 * yields an empty light list — the Dark/Light toggle is NOT disabled for it
 * (spec §2), the light slot falls through to the style's own default.
 */
export function themesForMode(mode: 'dark' | 'light', styleId: string = IDENTITY_STYLE_ID): AppTheme[] {
  return THEMES.filter((t) => t.mode === mode && themeStyleId(t) === styleId);
}

/**
 * Canonical theme id for a stored slot value. Slots are MODE-RESTRICTED
 * (unlike the old preset slots): a light theme's UI tokens under dark-scoped
 * CSS (dark --s-* set, starfield, color-scheme) would break validated
 * contrast, so unknown ids AND cross-mode ids normalize to the mode default.
 *
 * They are now STYLE-restricted for the same reason: a GLASS palette painted
 * under Blueprint's materials (or the reverse) is a combination nobody
 * curated and no gate measured. A cross-style id normalizes to the painted
 * style's own default.
 */
export function normalizeThemeId(
  id: string | undefined,
  mode: 'dark' | 'light',
  styleId: string = IDENTITY_STYLE_ID,
): string {
  const t = id ? byId.get(id) : undefined;
  if (t && t.mode === mode && themeStyleId(t) === styleId) return t.id;
  return defaultThemeFor(styleId, mode);
}

/** Resolve a stored slot value + mode (+ painted style) to a theme. Never
 * returns undefined. */
export function resolveTheme(
  id: string | undefined,
  mode: 'dark' | 'light',
  styleId: string = IDENTITY_STYLE_ID,
): AppTheme {
  return byId.get(normalizeThemeId(id, mode, styleId)) ?? MISSION_CONTROL;
}

/** Every custom property a theme may write — the apply path REMOVES all of
 * these that the active theme doesn't set, so switching back to a built-in
 * (or off a theme with overrides) fully clears the previous inline set. */
export const THEME_VAR_NAMES: readonly string[] = [
  ...Object.values(UI_TOKEN_VARS),
  ...OVERRIDABLE_TOKENS,
  '--send',
];

/** The inline CSS custom-property set for a theme. Built-ins get `{}` — the
 * styles.css block IS their definition, so they cannot drift at runtime.
 * NOTE: emits `--send`, never `--pi-send` — .piview declares
 * `--pi-send: var(--send)` element-own, which would mask an inline
 * `--pi-send` on <html>. */
export function themeCssVars(t: AppTheme): Record<string, string> {
  if (t.builtin) return {};
  const vars: Record<string, string> = {};
  for (const [key, name] of Object.entries(UI_TOKEN_VARS) as [keyof UiTokens, string][]) {
    vars[name] = t.ui[key];
  }
  for (const [name, value] of Object.entries(t.overrides ?? {})) {
    if (value) vars[name] = value;
  }
  if (t.send) vars['--send'] = t.send;
  return vars;
}
